<template>
  <div class="page-container insurance-plan-page">
    <portal to="page-name">Gerenciamento de plano de saúde</portal>
    <div class="loading loading-lg mt-2" v-if="loading" />
    <div class="card card-page" v-else>
      <div class="card-header ip-head">
        <div class="ip-head-main">
          <div class="ip-head-eyebrow" v-if="!isNew && form.insurance.name">
            {{ form.insurance.name }}
          </div>
          <div class="card-title">{{ getTitle }}</div>
          <div class="ip-head-hint">
            Configure carteirinha, guias, prazos de pagamento e tabelas de preço deste plano
          </div>
          <div class="ip-chips">
            <span class="ip-chip">{{ insuranceTypes[form.type] || 'Sem tipo' }}</span>
            <span class="ip-chip" v-if="form.record">ANS {{ form.record }}</span>
            <span class="ip-chip" :class="{'is-on': form.settings.tiss.enabled}"
                  v-if="isInsurance">
              <fa-icon :icon="['fal', form.settings.tiss.enabled
                ? 'shield-check' : 'file-alt']"/>
              {{ form.settings.tiss.enabled
                ? `TISS ${tissVersions[form.settings.tiss.version] || 'sem versão'}`
                : 'Guias genéricas' }}
            </span>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div class="ip-errors" v-if="submitted && totalErrors > 0">
          <div class="ip-errors-head">
            <fa-icon :icon="['fal', 'exclamation-triangle']"/>
            <strong>
              Não dá para salvar: {{ totalErrors }}
              {{ totalErrors === 1 ? 'campo precisa' : 'campos precisam' }} de correção
            </strong>
          </div>
          <div class="ip-errors-list">
            <button type="button" class="ip-error-field"
                    v-for="item in invalidFields" :key="item.id"
                    @click="goToField(item)">
              {{ item.label }}
              <small>{{ sectionName(item.section) }}</small>
            </button>
          </div>
        </div>
        <st-tabs id="plan-tabs" ref="plan-tabs" class="mt-2">
          <st-tab id="tab-data" name="Dados principais"
                  :badge="sectionErrors('tab-data')" badge-type="alert">
            <section class="ip-section">
              <header class="ip-section-head">
                <span class="ip-section-icon"><fa-icon :icon="['fal', 'id-card']"/></span>
                <span class="ip-section-text">
                  <strong>Identificação</strong>
                  <small>Operadora, nome e tipo deste plano</small>
                </span>
              </header>
              <div class="ip-section-body">
                <div class="columns">
                  <div class="column col-6 col-md-12 col-sm-12 form-group"
                       :class="{'has-error': $v.form.insurance.id.$error}">
                    <label class="form-label" for="plan-insurance">
                      Operadora ou agrupador
                    </label>
                    <div class="input-group">
                      <select id="plan-insurance" class="form-select"
                              v-model="form.insurance.id"
                              @change="changeInsurance"
                              @blur="$v.form.insurance.id.$touch()"
                              :disabled="!isNew"
                      >
                        <option value="">Selecione</option>
                        <option value="is_new">Cadastrar nova operadora</option>
                        <option v-for="(item, i) in insurances"
                                :value="item.id"
                                :key="i"
                        >{{ item.name }}
                        </option>
                      </select>
                      <button type="button" class="btn btn-gray-outline input-group-btn"
                              :disabled="!isNew"
                              @click="openInsuranceModal">Gerenciar</button>
                    </div>
                    <div class="form-input-hint" v-if="$v.form.insurance.id.$error">
                      Campo obrigatório
                    </div>
                    <div class="form-input-hint" v-else-if="!isNew">
                      A operadora não muda depois que o plano é criado
                    </div>
                  </div>
                  <div class="column col-6 col-md-12 col-sm-12 form-group"
                       :class="{'has-error': $v.form.name.$error}">
                    <label class="form-label" for="plan-name">Nome do plano</label>
                    <input type="text" id="plan-name"
                           class="form-input"
                           v-model="form.name"
                           @blur="$v.form.name.$touch()"
                           maxlength="60"
                           placeholder="Nome do plano ou particular"
                    >
                    <div class="form-input-hint" v-if="$v.form.name.$error">
                      Campo obrigatório
                    </div>
                  </div>
                  <div class="column col-3 col-md-6 col-sm-12 form-group"
                       :class="{'has-error': $v.form.type.$error}">
                    <label class="form-label" for="plan-type">Tipo</label>
                    <select id="plan-type" class="form-select"
                            v-model="form.type"
                            @change="changeType"
                            @blur="$v.form.type.$touch()"
                    >
                      <option value="">Selecione</option>
                      <option v-for="(text, value) in insuranceTypes"
                              :value="value"
                              :key="value"
                      >{{ text }}
                      </option>
                    </select>
                    <div class="form-input-hint" v-if="$v.form.type.$error">
                      Campo obrigatório
                    </div>
                  </div>
                  <div class="column col-3 col-md-6 col-sm-12 form-group"
                       :class="{'has-error': $v.form.record.$error}">
                    <label class="form-label" for="plan-record">Registro ANS</label>
                    <input type="text" id="plan-record"
                           class="form-input"
                           v-model="form.record"
                           :disabled="!isInsurance"
                           @blur="$v.form.record.$touch()"
                           v-mask-number
                           maxlength="6"
                           placeholder="000000"
                    >
                    <div class="form-input-hint" v-if="$v.form.record.$error">
                      Campo obrigatório
                    </div>
                    <div class="form-input-hint" v-else-if="!isInsurance">
                      Só para planos do tipo convênio
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section class="ip-section">
              <header class="ip-section-head">
                <span class="ip-section-icon"><fa-icon :icon="['fal', 'file-alt']"/></span>
                <span class="ip-section-text">
                  <strong>Faturamento das guias</strong>
                  <small>Define se este plano gera XML no padrão TISS</small>
                </span>
              </header>
              <div class="ip-section-body">
                <div class="columns">
                  <div class="column col-4 col-md-6 col-sm-12 form-group">
                    <label class="form-label" for="plan-tiss">Padrão de faturamento</label>
                    <select id="plan-tiss" class="form-select"
                            v-model="form.settings.tiss.enabled"
                            :disabled="!isInsurance">
                      <option :value="false">Guias genéricas</option>
                      <option :value="true">Padrão TISS (gera XML)</option>
                    </select>
                  </div>
                  <div class="column col-3 col-md-6 col-sm-12 form-group"
                       :class="{'has-error': $v.form.settings.tiss.version.$error}">
                    <label class="form-label" for="version">Versão TISS</label>
                    <select id="version" class="form-select"
                            v-model="form.settings.tiss.version"
                            :disabled="!isInsurance || !form.settings.tiss.enabled"
                            @blur="$v.form.settings.tiss.version.$touch()">
                      <option value="">[Selecione]</option>
                      <option v-for="(text, value) in tissVersions"
                              :value="value" :key="value">{{ text }}</option>
                    </select>
                    <div class="form-input-hint" v-if="$v.form.settings.tiss.version.$error">
                      Campo obrigatório
                    </div>
                  </div>
                </div>
                <div class="ip-summary">
                  <fa-icon :icon="['fal', 'info-circle']"/>
                  <span>{{ planSummary }}</span>
                </div>
              </div>
            </section>

            <section class="ip-section">
              <header class="ip-section-head">
                <span class="ip-section-icon"><fa-icon :icon="['fal', 'comment-lines']"/></span>
                <span class="ip-section-text">
                  <strong>Observações</strong>
                  <small>Anotações internas sobre o contrato ou o plano</small>
                </span>
              </header>
              <div class="ip-section-body">
                <div class="form-group">
                  <textarea class="form-input ip-notes" rows="3"
                            v-model="form.notes"></textarea>
                </div>
              </div>
            </section>
          </st-tab>

          <st-tab id="tab-attendance" name="Agenda e atendimento"
                  :badge="sectionErrors('tab-attendance')" badge-type="alert">
            <section class="ip-section">
              <header class="ip-section-head">
                <span class="ip-section-icon"><fa-icon :icon="['fal', 'address-card']"/></span>
                <span class="ip-section-text">
                  <strong>Carteirinha</strong>
                  <small>O que a recepção é obrigada a preencher no agendamento</small>
                </span>
              </header>
              <div class="ip-section-body">
                <div class="ip-switches">
                  <label class="ip-switch-row" for="sw-required">
                    <span class="ip-switch-text">
                      <strong>Exigir a carteirinha</strong>
                      <small>O atendimento não é salvo sem o número da carteirinha</small>
                    </span>
                    <span class="form-switch no-label">
                      <input type="checkbox" id="sw-required"
                             v-model="form.settings.general.record.required">
                      <i class="form-icon"></i>
                    </span>
                  </label>
                  <label class="ip-switch-row" for="sw-required-validity">
                    <span class="ip-switch-text">
                      <strong>Exigir a validade da carteirinha</strong>
                      <small>Obriga informar até quando a carteirinha vale</small>
                    </span>
                    <span class="form-switch no-label">
                      <input type="checkbox" id="sw-required-validity"
                             v-model="form.settings.general.record.requiredValidity">
                      <i class="form-icon"></i>
                    </span>
                  </label>
                  <label class="ip-switch-row" for="sw-fill-validity-with-service-date">
                    <span class="ip-switch-text">
                      <strong>Preencher a validade com a data do atendimento</strong>
                      <small>Use quando a operadora aceita a carteirinha válida no dia</small>
                    </span>
                    <span class="form-switch no-label">
                      <input type="checkbox" id="sw-fill-validity-with-service-date"
                             v-model="form.settings.general.record.fillValidityWithServiceDate">
                      <i class="form-icon"></i>
                    </span>
                  </label>
                  <label class="ip-switch-row" for="sw-record-digits-enabled">
                    <span class="ip-switch-text">
                      <strong>Validar a quantidade de dígitos</strong>
                      <small>Avisa a recepção quando o número não tem o tamanho esperado</small>
                    </span>
                    <span class="ip-switch-field" v-if="recordDigitsActive">
                      <span class="ip-switch-inline">
                        <span :class="{'has-error': $v.form.settings.general
                              .record.digits.min.$error}">
                          <dx-input-number id="record-digits-min"
                                           class="form-input text-center"
                                           maxlength="3"
                                           placeholder="mín"
                                           @blur="$v.form.settings.general
                                           .record.digits.min.$touch()"
                                           v-model="form.settings.general.record.digits.min"
                          />
                        </span>
                        <span :class="{'has-error': $v.form.settings.general
                              .record.digits.max.$error}">
                          <dx-input-number id="record-digits-max"
                                           class="form-input text-center"
                                           maxlength="3"
                                           placeholder="máx"
                                           @blur="$v.form.settings.general
                                           .record.digits.max.$touch()"
                                           v-model="form.settings.general.record.digits.max"
                          />
                        </span>
                      </span>
                      <span class="form-input-hint">mínimo e máximo</span>
                    </span>
                    <span class="form-switch no-label">
                      <input type="checkbox" id="sw-record-digits-enabled"
                             v-model="form.settings.general.record.digits.enabled">
                      <i class="form-icon"></i>
                    </span>
                  </label>
                </div>
              </div>
            </section>

            <section class="ip-section">
              <header class="ip-section-head">
                <span class="ip-section-icon"><fa-icon :icon="['fal', 'history']"/></span>
                <span class="ip-section-text">
                  <strong>Retorno</strong>
                  <small>Prazo em que o paciente volta sem gerar nova cobrança</small>
                </span>
              </header>
              <div class="ip-section-body">
                <div class="columns">
                  <div class="column col-12 form-group ip-inline-field"
                       :class="{'has-error': $v.form.returnDays.$error}">
                    <label class="form-label" for="plan-return-days">
                      Dias de retorno
                    </label>
                    <div class="input-group">
                      <dx-input-number
                        id="plan-return-days"
                        class="form-input text-center"
                        v-model="form.returnDays"
                        maxlength="3"
                        placeholder="—"
                        @blur="$v.form.returnDays.$touch()"
                      />
                      <span class="input-group-addon">dias</span>
                    </div>
                    <div class="form-input-hint" v-if="$v.form.returnDays.$error">
                      Informe entre 1 e 365 dias
                    </div>
                    <div class="form-input-hint" v-else>{{ returnDaysHint }}</div>
                  </div>
                </div>
                <div class="ip-switches" v-if="isInsurance">
                  <label class="ip-switch-row" for="sw-return-enabled">
                    <span class="ip-switch-text">
                      <strong>Pedir senha de liberação fora do prazo</strong>
                      <small>
                        Retorno acima do prazo só é aceito com autorização da operadora
                      </small>
                    </span>
                    <span class="form-switch no-label">
                      <input type="checkbox" id="sw-return-enabled"
                             v-model="form.settings.general.billing.return.enabled">
                      <i class="form-icon"></i>
                    </span>
                  </label>
                </div>
              </div>
            </section>

            <section class="ip-section" v-if="isInsurance">
              <header class="ip-section-head">
                <span class="ip-section-icon"><fa-icon :icon="['fal', 'eye-slash']"/></span>
                <span class="ip-section-text">
                  <strong>Valores no atendimento</strong>
                  <small>O que a recepção vê e pode alterar na tela do paciente</small>
                </span>
              </header>
              <div class="ip-section-body">
                <div class="ip-switches">
                  <label class="ip-switch-row" for="sw-block-values">
                    <span class="ip-switch-text">
                      <strong>Impedir a edição dos valores</strong>
                      <small>Os valores vêm da tabela de preço e ficam travados na guia</small>
                    </span>
                    <span class="form-switch no-label">
                      <input type="checkbox" id="sw-block-values"
                             v-model="form.settings.general.billing.blockValues">
                      <i class="form-icon"></i>
                    </span>
                  </label>
                  <label class="ip-switch-row" for="sw-hide-insurance-value">
                    <span class="ip-switch-text">
                      <strong>Ocultar o valor do convênio</strong>
                      <small>Esconde quanto a operadora paga nas telas de atendimento</small>
                    </span>
                    <span class="form-switch no-label">
                      <input type="checkbox" id="sw-hide-insurance-value"
                             v-model="form.settings.general.billing.hideInsuranceValue">
                      <i class="form-icon"></i>
                    </span>
                  </label>
                </div>
              </div>
            </section>

            <section class="ip-section">
              <header class="ip-section-head">
                <span class="ip-section-icon"><fa-icon :icon="['fal', 'user-hard-hat']"/></span>
                <span class="ip-section-text">
                  <strong>ASO</strong>
                  <small>Atendimentos de saúde ocupacional</small>
                </span>
              </header>
              <div class="ip-section-body">
                <div class="ip-switches">
                  <label class="ip-switch-row" for="sw-required-client">
                    <span class="ip-switch-text">
                      <strong>Exigir a empresa na agenda</strong>
                      <small>O agendamento só é salvo com a empresa do exame informada</small>
                    </span>
                    <span class="form-switch no-label">
                      <input type="checkbox" id="sw-required-client"
                             v-model="form.settings.general.aso.requiredClient">
                      <i class="form-icon"></i>
                    </span>
                  </label>
                </div>
              </div>
            </section>
          </st-tab>

          <st-tab id="tab-tiss" name="Guias TISS"
                  :badge="sectionErrors('tab-tiss')" badge-type="alert">
            <div class="ip-gate" v-if="!isInsurance">
              <span class="ip-gate-icon"><fa-icon :icon="['fal', 'lock']"/></span>
              <span class="ip-gate-text">
                <strong>Este plano não é um convênio</strong>
                <small>As guias TISS valem para planos do tipo convênio.</small>
              </span>
              <button type="button" class="btn btn-sm btn-secondary"
                      @click="goToSection('tab-data')">Alterar o tipo</button>
            </div>
            <div class="ip-gate" v-else-if="!form.settings.tiss.enabled">
              <span class="ip-gate-icon"><fa-icon :icon="['fal', 'lock']"/></span>
              <span class="ip-gate-text">
                <strong>Este plano usa guias genéricas</strong>
                <small>
                  As regras de contrato e de preenchimento da guia só valem no padrão TISS.
                </small>
              </span>
              <button type="button" class="btn btn-sm btn-secondary"
                      @click="goToSection('tab-data')">Ligar o padrão TISS</button>
            </div>
            <template v-else>
              <section class="ip-section">
                <header class="ip-section-head">
                  <span class="ip-section-icon"><fa-icon :icon="['fal', 'handshake']"/></span>
                  <span class="ip-section-text">
                    <strong>Contrato com a operadora</strong>
                    <small>Identificação do prestador no XML enviado</small>
                  </span>
                  <button type="button"
                          class="btn btn-sm btn-gray btn-icon btn-icon-left ip-section-action"
                          :disabled="!isCompany || loadingBranch"
                          :class="{loading: loadingBranch}"
                          @click="getCompanyData">
                    <fa-icon :icon="['fal', 'sync']" />Preencher com os dados da empresa
                  </button>
                </header>
                <div class="ip-section-body">
                  <div class="columns">
                    <div class="column col-1 col-md-2 col-sm-4 form-group">
                      <label class="form-label" for="contract-type">Tipo</label>
                      <select id="contract-type" class="form-select"
                              v-model="form.settings.tiss.contract.type">
                        <option value="person">PF</option>
                        <option value="company">PJ</option>
                      </select>
                    </div>
                    <div class="column col-2 col-md-4 col-sm-6 form-group"
                         :class="{'has-error': $v.form.settings.tiss.contract.code.$error}">
                      <label class="form-label" for="operator">Código na operadora</label>
                      <input type="text" id="operator" class="form-input"
                             v-model="form.settings.tiss.contract.code"
                             maxlength="20"
                             :disabled="!isCompany"
                             @blur="$v.form.settings.tiss.contract.code.$touch()" />
                      <div class="form-input-hint"
                           v-if="$v.form.settings.tiss.contract.code.$error">
                        Campo obrigatório
                      </div>
                      <div class="form-input-hint" v-else-if="!isCompany">
                        Só para contrato PJ
                      </div>
                    </div>
                    <div class="column col-4 col-md-4 col-sm-6 form-group"
                         :class="{'has-error': $v.form.settings.tiss.contract.identity.$error}">
                      <label class="form-label" for="identity">CNPJ</label>
                      <input type="text" id="identity" class="form-input"
                             v-model="form.settings.tiss.contract.identity"
                             v-mask="'00.000.000/0000-00'"
                             :disabled="!isCompany"
                             @blur="$v.form.settings.tiss.contract.identity.$touch()" />
                      <div class="form-input-hint"
                           v-if="$v.form.settings.tiss.contract.identity.$error">
                        Informe um CNPJ válido
                      </div>
                    </div>
                    <div class="column col-3 col-md-8 col-sm-12 form-group"
                         :class="{'has-error': $v.form.settings.tiss.contract.name.$error}">
                      <label class="form-label" for="name">Nome</label>
                      <input type="text" id="name" class="form-input"
                             v-model="form.settings.tiss.contract.name"
                             maxlength="70"
                             :disabled="!isCompany"
                             @blur="$v.form.settings.tiss.contract.name.$touch()" />
                      <div class="form-input-hint"
                           v-if="$v.form.settings.tiss.contract.name.$error">
                        Campo obrigatório
                      </div>
                    </div>
                    <div class="column col-2 col-md-3 col-sm-6 form-group"
                         :class="{'has-error': $v.form.settings.tiss.contract.cnes.$error}">
                      <label class="form-label" for="cnes">CNES</label>
                      <input type="text" id="cnes" class="form-input"
                             v-model="form.settings.tiss.contract.cnes"
                             v-mask-number
                             maxlength="7"
                             @blur="$v.form.settings.tiss.contract.cnes.$touch()" />
                      <div class="form-input-hint"
                           v-if="$v.form.settings.tiss.contract.cnes.$error">
                        Campo obrigatório
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section class="ip-section">
                <header class="ip-section-head">
                  <span class="ip-section-icon"><fa-icon :icon="['fal', 'file-invoice']"/></span>
                  <span class="ip-section-text">
                    <strong>Dados que vão na guia</strong>
                    <small>O que o XML leva em cada campo de identificação</small>
                  </span>
                </header>
                <div class="ip-section-body">
                  <div class="columns">
                    <div class="column col-3 col-md-6 col-sm-12 form-group">
                      <label class="form-label" for="origin">Origem</label>
                      <select id="origin" class="form-select"
                              v-model="referralOrigin">
                        <option v-for="(text) in tissTags"
                                :value="text.key" :key="text.key">{{ text.name }}</option>
                      </select>
                    </div>
                    <div class="column col-3 col-md-6 col-sm-12 form-group">
                      <label class="form-label" for="requester">Dados do solicitante</label>
                      <select id="requester" class="form-select"
                              v-model="referralRequester">
                        <option v-for="(text) in tissTags"
                                :value="text.key" :key="text.key">{{ text.name }}</option>
                      </select>
                    </div>
                    <div class="column col-3 col-md-6 col-sm-12 form-group">
                      <label class="form-label" for="executant">Dados do executante</label>
                      <select id="executant" class="form-select"
                              v-model="referralExecutant">
                        <option v-for="(text) in tissTags"
                                :value="text.key" :key="text.key">{{ text.name }}</option>
                      </select>
                    </div>
                    <div class="column col-3 col-md-6 col-sm-12 form-group">
                      <label class="form-label" for="professional-executant">
                        Profissional executante
                      </label>
                      <select id="professional-executant" class="form-select"
                              v-model="referralProfessional">
                        <option v-for="(text) in tissTags"
                                :value="text.key" :key="text.key">{{ text.name }}</option>
                      </select>
                    </div>
                  </div>
                  <div class="form-input-hint">
                    A operadora define se cada campo leva o documento ou o código cadastrado
                  </div>
                </div>
              </section>

              <section class="ip-section">
                <header class="ip-section-head">
                  <span class="ip-section-icon"><fa-icon :icon="['fal', 'sparkles']"/></span>
                  <span class="ip-section-text">
                    <strong>Preenchimento automático</strong>
                    <small>O que o sistema completa sozinho ao montar a guia</small>
                  </span>
                </header>
                <div class="ip-section-body">
                  <div class="ip-switches">
                    <label class="ip-switch-row" for="sw-sadt">
                      <span class="ip-switch-text">
                        <strong>Faturar consultas em guias SP/SADT</strong>
                        <small>A consulta entra como procedimento na guia SP/SADT</small>
                      </span>
                      <span class="form-switch no-label">
                        <input type="checkbox" id="sw-sadt"
                               v-model="form.settings.tiss.consultation.sadt">
                        <i class="form-icon"></i>
                      </span>
                    </label>
                    <label class="ip-switch-row" for="sw-fill-validity-date-enabled">
                      <span class="ip-switch-text">
                        <strong>Calcular a validade da senha</strong>
                        <small>
                          Preenche a "6 - Data de validade" com a "4 - Data da autorização"
                          mais os dias informados
                        </small>
                      </span>
                      <span class="ip-switch-field" v-if="fillValidityDateActive"
                            :class="{'has-error': $v.form.settings.tiss.sadt.authorization
                            .fillValidityDate.days.$error}">
                        <span class="input-group">
                          <dx-input-number
                            id="tiss-validity-days"
                            class="form-input text-center"
                            maxlength="3"
                            v-model="form.settings.tiss.sadt.authorization.fillValidityDate.days"
                            @blur="$v.form.settings.tiss.sadt.authorization
                            .fillValidityDate.days.$touch()"
                          />
                          <span class="input-group-addon">dias</span>
                        </span>
                      </span>
                      <span class="form-switch no-label">
                        <input type="checkbox" id="sw-fill-validity-date-enabled"
                               v-model="form.settings.tiss.sadt.authorization
                               .fillValidityDate.enabled">
                        <i class="form-icon"></i>
                      </span>
                    </label>
                    <label class="ip-switch-row" for="sw-fill-request-data-with-service-date">
                      <span class="ip-switch-text">
                        <strong>Preencher a data da solicitação com a data do atendimento</strong>
                        <small>Vale para a guia SP/SADT</small>
                      </span>
                      <span class="form-switch no-label">
                        <input type="checkbox" id="sw-fill-request-data-with-service-date"
                               v-model="form.settings.tiss.sadt.fillRequestDataWithServiceDate">
                        <i class="form-icon"></i>
                      </span>
                    </label>
                    <label class="ip-switch-row" for="sw-fill-authorization-date-with-service-date">
                      <span class="ip-switch-text">
                        <strong>Preencher a data da autorização com a data do atendimento</strong>
                        <small>Use quando a operadora autoriza no próprio dia</small>
                      </span>
                      <span class="form-switch no-label">
                        <input type="checkbox" id="sw-fill-authorization-date-with-service-date"
                               v-model="form.settings.tiss.sadt
                               .fillAuthorizationDateWithServiceDate">
                        <i class="form-icon"></i>
                      </span>
                    </label>
                    <label class="ip-switch-row" for="sw-fill-insurance-referral-with-password">
                      <span class="ip-switch-text">
                        <strong>Preencher a guia da operadora com a senha</strong>
                        <small>A senha de autorização vai também no número da guia</small>
                      </span>
                      <span class="form-switch no-label">
                        <input type="checkbox" id="sw-fill-insurance-referral-with-password"
                               v-model="form.settings.tiss.sadt.authorization
                               .fillInsuranceReferralWithPassword">
                        <i class="form-icon"></i>
                      </span>
                    </label>
                  </div>
                </div>
              </section>

              <section class="ip-section">
                <header class="ip-section-head">
                  <span class="ip-section-icon">
                    <fa-icon :icon="['fal', 'clipboard-list-check']"/>
                  </span>
                  <span class="ip-section-text">
                    <strong>Campos obrigatórios na guia</strong>
                    <small>O que a operadora não aceita em branco</small>
                  </span>
                </header>
                <div class="ip-section-body">
                  <div class="ip-block-title">Guia de consulta</div>
                  <div class="ip-switches">
                    <label class="ip-switch-row" for="sw-required-insurance-referral">
                      <span class="ip-switch-text">
                        <strong>Exigir o número da guia da operadora</strong>
                        <small>A consulta não fecha sem a guia informada pela operadora</small>
                      </span>
                      <span class="form-switch no-label">
                        <input type="checkbox" id="sw-required-insurance-referral"
                               v-model="form.settings.tiss.consultation
                               .requiredInsuranceReferral">
                        <i class="form-icon"></i>
                      </span>
                    </label>
                  </div>
                  <div class="ip-block">
                    <div class="ip-block-title">Guia SP/SADT</div>
                    <div class="ip-switches">
                      <label class="ip-switch-row" for="sw-required-request-date">
                        <span class="ip-switch-text">
                          <strong>Exigir a data da solicitação</strong>
                          <small>Campo "1 - Data da solicitação" da guia</small>
                        </span>
                        <span class="form-switch no-label">
                          <input type="checkbox" id="sw-required-request-date"
                                 v-model="form.settings.tiss.sadt.requiredRequestDate">
                          <i class="form-icon"></i>
                        </span>
                      </label>
                      <label class="ip-switch-row" for="sw-required-date">
                        <span class="ip-switch-text">
                          <strong>Exigir a data da autorização</strong>
                          <small>Campo "4 - Data da autorização" da guia</small>
                        </span>
                        <span class="form-switch no-label">
                          <input type="checkbox" id="sw-required-date"
                                 v-model="form.settings.tiss.sadt.authorization.requiredDate">
                          <i class="form-icon"></i>
                        </span>
                      </label>
                      <label class="ip-switch-row" for="sw-required-password">
                        <span class="ip-switch-text">
                          <strong>Exigir a senha de autorização</strong>
                          <small>Campo "5 - Senha" da guia</small>
                        </span>
                        <span class="form-switch no-label">
                          <input type="checkbox" id="sw-required-password"
                                 v-model="form.settings.tiss.sadt.authorization.requiredPassword">
                          <i class="form-icon"></i>
                        </span>
                      </label>
                      <label class="ip-switch-row" for="sw-auth-required-validity">
                        <span class="ip-switch-text">
                          <strong>Exigir a validade da senha</strong>
                          <small>Campo "6 - Data de validade da senha" da guia</small>
                        </span>
                        <span class="form-switch no-label">
                          <input type="checkbox" id="sw-auth-required-validity"
                                 v-model="form.settings.tiss.sadt.authorization.requiredValidity">
                          <i class="form-icon"></i>
                        </span>
                      </label>
                      <label class="ip-switch-row" for="sw-auth-insurance-referral">
                        <span class="ip-switch-text">
                          <strong>Exigir a guia da operadora</strong>
                          <small>Campo "3 - Número da guia atribuído pela operadora"</small>
                        </span>
                        <span class="form-switch no-label">
                          <input type="checkbox" id="sw-auth-insurance-referral"
                                 v-model="form.settings.tiss.sadt.authorization
                                 .requiredInsuranceReferral">
                          <i class="form-icon"></i>
                        </span>
                      </label>
                      <label class="ip-switch-row" for="sw-required-main-referral">
                        <span class="ip-switch-text">
                          <strong>Exigir a guia principal</strong>
                          <small>Campo "2 - Número da guia principal" da guia</small>
                        </span>
                        <span class="form-switch no-label">
                          <input type="checkbox" id="sw-required-main-referral"
                                 v-model="form.settings.tiss.sadt.authorization
                                 .requiredMainReferral">
                          <i class="form-icon"></i>
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </section>
            </template>
          </st-tab>

          <st-tab id="tab-billing" name="Faturamento"
                  :badge="sectionErrors('tab-billing')" badge-type="alert">
            <div class="ip-gate" v-if="!isInsurance">
              <span class="ip-gate-icon"><fa-icon :icon="['fal', 'lock']"/></span>
              <span class="ip-gate-text">
                <strong>Este plano não é um convênio</strong>
                <small>Lote, prazos de pagamento e glosas valem para planos de convênio.</small>
              </span>
              <button type="button" class="btn btn-sm btn-secondary"
                      @click="goToSection('tab-data')">Alterar o tipo</button>
            </div>
            <template v-else>
              <section class="ip-section">
                <header class="ip-section-head">
                  <span class="ip-section-icon"><fa-icon :icon="['fal', 'sort-numeric-up-alt']"/>
                  </span>
                  <span class="ip-section-text">
                    <strong>Número da guia do prestador</strong>
                    <small>De onde sai o número que identifica a guia na sua clínica</small>
                  </span>
                </header>
                <div class="ip-section-body">
                  <div class="ip-choice-rows">
                    <label class="ip-choice-row" for="provider-manual"
                           :class="{'is-active': providerReferralMode === 'manual'}">
                      <span class="form-radio no-label">
                        <input id="provider-manual" name="radio-provider-guide"
                               type="radio" value="manual"
                               v-model="form.settings.general.providerReferral.mode">
                        <i class="form-icon"></i>
                      </span>
                      <span class="ip-choice-text">
                        <strong>Informar manualmente</strong>
                        <small>A recepção digita o número em cada guia</small>
                      </span>
                    </label>
                    <label class="ip-choice-row" for="provider-automatic"
                           :class="{'is-active': providerReferralActive}">
                      <span class="form-radio no-label">
                        <input id="provider-automatic" name="radio-provider-guide"
                               type="radio" value="automatic"
                               v-model="form.settings.general.providerReferral.mode">
                        <i class="form-icon"></i>
                      </span>
                      <span class="ip-choice-text">
                        <strong>Gerar automaticamente</strong>
                        <small>O sistema numera em sequência a partir do número atual</small>
                      </span>
                      <span class="ip-choice-field" v-if="providerReferralActive"
                            :class="{'has-error': $v.form.settings.general
                            .providerReferral.lastValue.$error}">
                        <input type="text"
                               id="provider-last-value"
                               class="form-input text-center"
                               @blur="$v.form.settings.general.providerReferral
                               .lastValue.$touch()"
                               v-model="form.settings.general.providerReferral.lastValue"
                               v-mask="'000000000000000'"
                        />
                        <span class="form-input-hint">guia atual</span>
                      </span>
                    </label>
                    <label class="ip-choice-row" for="provider-password"
                           :class="{'is-active': providerReferralMode === 'password'}">
                      <span class="form-radio no-label">
                        <input id="provider-password" name="radio-provider-guide"
                               type="radio" value="password"
                               v-model="form.settings.general.providerReferral.mode">
                        <i class="form-icon"></i>
                      </span>
                      <span class="ip-choice-text">
                        <strong>Usar a senha de autorização</strong>
                        <small>A senha da operadora vira o número da guia do prestador</small>
                      </span>
                    </label>
                    <label class="ip-choice-row" for="provider-insurance-referral"
                           :class="{'is-active': providerReferralMode === 'insurance_referral'}">
                      <span class="form-radio no-label">
                        <input id="provider-insurance-referral" name="radio-provider-guide"
                               type="radio" value="insurance_referral"
                               v-model="form.settings.general.providerReferral.mode">
                        <i class="form-icon"></i>
                      </span>
                      <span class="ip-choice-text">
                        <strong>Usar a guia da operadora</strong>
                        <small>Repete o número que a operadora atribuiu</small>
                      </span>
                    </label>
                  </div>
                </div>
              </section>

              <section class="ip-section">
                <header class="ip-section-head">
                  <span class="ip-section-icon"><fa-icon :icon="['fal', 'layer-group']"/></span>
                  <span class="ip-section-text">
                    <strong>Fechamento do lote</strong>
                    <small>Como as guias são agrupadas antes do envio</small>
                  </span>
                </header>
                <div class="ip-section-body">
                  <div class="columns">
                    <div class="column col-12 form-group ip-inline-field"
                         :class="{'has-error': $v.form.settings.general.billing
                         .submissionDays.$error}">
                      <label class="form-label" for="plan-submission-days">
                        Prazo de apresentação
                      </label>
                      <div class="input-group">
                        <dx-input-number
                          id="plan-submission-days"
                          class="form-input text-center"
                          maxlength="3"
                          placeholder="90"
                          @blur="$v.form.settings.general.billing.submissionDays.$touch()"
                          v-model="form.settings.general.billing.submissionDays"
                        />
                        <span class="input-group-addon">dias</span>
                      </div>
                      <div class="form-input-hint"
                           v-if="$v.form.settings.general.billing.submissionDays.$error">
                        {{ submissionDaysHint }}
                      </div>
                      <div class="form-input-hint" v-else>
                        Contado da data do atendimento; depois disso a guia aparece como vencida
                      </div>
                    </div>
                  </div>
                  <div class="ip-switches">
                    <label class="ip-switch-row" for="sw-allow-different-referral-types">
                      <span class="ip-switch-text">
                        <strong>Aceita lotes mistos</strong>
                        <small>
                          Guias de consulta e SP/SADT no mesmo lote. A maioria das operadoras
                          exige lotes separados por tipo de guia.
                        </small>
                      </span>
                      <span class="form-switch no-label">
                        <input type="checkbox" id="sw-allow-different-referral-types"
                               v-model="form.settings.general.billing.allowDifferentReferralTypes">
                        <i class="form-icon"></i>
                      </span>
                    </label>
                    <label class="ip-switch-row" for="sw-accounts-enabled">
                      <span class="ip-switch-text">
                        <strong>Limitar a quantidade de guias por lote</strong>
                        <small>O lote fecha ao atingir a quantidade informada</small>
                      </span>
                      <span class="ip-switch-field" v-if="billingAccountsActive"
                            :class="{'has-error': $v.form.settings.general
                            .billing.accounts.quantity.$error}">
                        <span class="input-group">
                          <dx-input-number
                            id="plan-accounts-quantity"
                            class="form-input text-center"
                            maxlength="3"
                            @blur="$v.form.settings.general.billing.accounts.quantity.$touch()"
                            v-model="form.settings.general.billing.accounts.quantity"
                          />
                          <span class="input-group-addon">guias</span>
                        </span>
                      </span>
                      <span class="form-switch no-label">
                        <input type="checkbox" id="sw-accounts-enabled"
                               v-model="form.settings.general.billing.accounts.enabled">
                        <i class="form-icon"></i>
                      </span>
                    </label>
                    <label class="ip-switch-row" for="sw-billing-for-individual-sessions">
                      <span class="ip-switch-text">
                        <strong>Faturamento por sessões individualizadas</strong>
                        <small>Cada sessão vira um item próprio na guia</small>
                      </span>
                      <span class="form-switch no-label">
                        <input type="checkbox" id="sw-billing-for-individual-sessions"
                               v-model="form.settings.general.billing
                               .billingForIndividualSessions">
                        <i class="form-icon"></i>
                      </span>
                    </label>
                  </div>
                </div>
              </section>

              <section class="ip-section ip-payment">
                <header class="ip-section-head">
                  <span class="ip-section-icon"><fa-icon :icon="['fal', 'calendar-check']"/></span>
                  <span class="ip-section-text">
                    <strong>Quando a operadora paga</strong>
                    <small>Escolha a regra que está no contrato</small>
                  </span>
                </header>
                <div class="ip-section-body">
                  <div class="rule-cards">
                    <div class="rule-card" v-for="option in paymentRuleTypes"
                         :key="option.value"
                         :class="{'is-active': payment.type === option.value}"
                         @click="payment.type = option.value">
                      <span class="rule-icon">
                        <fa-icon :icon="['fal', option.icon]"/>
                      </span>
                      <span class="rule-text">
                        <strong>{{ option.text }}</strong>
                        <small>{{ option.description }}</small>
                      </span>
                      <span class="rule-check" v-if="payment.type === option.value">
                        <fa-icon :icon="['fal', 'check-circle']"/>
                      </span>
                    </div>
                  </div>

                  <div class="columns rule-fields" v-if="payment.type === 'daysAfter'">
                    <div class="column col-5 col-md-12 form-group ip-inline-field"
                         :class="{'has-error': $v.form.settings.general.billing
                         .payment.daysAfter.days.$error}">
                      <label class="form-label" for="plan-payment-days">Dias</label>
                      <div class="input-group">
                        <dx-input-number
                          id="plan-payment-days"
                          class="form-input text-center"
                          maxlength="3"
                          placeholder="—"
                          @blur="$v.form.settings.general.billing.payment.daysAfter.days
                          .$touch()"
                          v-model="payment.daysAfter.days"
                        />
                        <span class="input-group-addon">dias</span>
                      </div>
                      <div class="form-input-hint"
                           v-if="$v.form.settings.general.billing
                           .payment.daysAfter.days.$error">
                        Informe entre 1 e 365 dias
                      </div>
                      <div class="form-input-hint" v-else>
                        Em branco usa o padrão da filial
                      </div>
                    </div>
                    <div class="column col-7 col-md-12 form-group ip-inline-field">
                      <label class="form-label" for="plan-payment-from">
                        Contados a partir de
                      </label>
                      <select id="plan-payment-from" class="form-select"
                              v-model="payment.daysAfter.from">
                        <option v-for="option in paymentMilestones" :key="option.value"
                                :value="option.value">{{ option.text }}</option>
                      </select>
                      <div class="form-input-hint">
                        Marco do contrato que inicia a contagem
                      </div>
                    </div>
                  </div>

                  <div class="rule-fields" v-else-if="payment.type === 'fixedDays'">
                    <div class="form-group"
                         :class="{'has-error': $v.form.settings.general.billing
                         .payment.fixedDays.days.$error}">
                      <label class="form-label">Dias do mês em que a operadora paga</label>
                      <div class="payment-days-grid" id="plan-fixed-days">
                        <button type="button" class="payment-day" v-for="day in 31"
                                :key="day"
                                :class="{active: payment.fixedDays.days.includes(day)}"
                                @click="toggleFixedDay(day)">{{ day }}</button>
                      </div>
                      <div class="form-input-hint"
                           v-if="$v.form.settings.general.billing
                           .payment.fixedDays.days.$error">
                        Selecione ao menos um dia
                      </div>
                      <div class="form-input-hint" v-else>{{ fixedDaysSummary }}</div>
                    </div>
                    <div class="columns">
                      <div class="column col-12 form-group ip-inline-field"
                           :class="{'has-error': $v.form.settings.general.billing
                           .payment.fixedDays.graceDays.$error}">
                        <label class="form-label" for="plan-payment-grace">
                          Carência
                        </label>
                        <div class="input-group">
                          <dx-input-number
                            id="plan-payment-grace"
                            class="form-input text-center"
                            maxlength="3"
                            placeholder="0"
                            @blur="$v.form.settings.general.billing.payment
                            .fixedDays.graceDays.$touch()"
                            v-model="payment.fixedDays.graceDays"
                          />
                          <span class="input-group-addon">dias</span>
                        </div>
                        <div class="form-input-hint"
                             v-if="$v.form.settings.general.billing.payment
                             .fixedDays.graceDays.$error">
                          Informe entre 0 e 365 dias
                        </div>
                        <div class="form-input-hint" v-else>
                          O lote só entra numa data fixa depois desse prazo
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="rule-fields payment-schedule" id="plan-schedule" v-else>
                    <div class="payment-schedule-head">
                      <span class="form-label">
                        Ciclos do cronograma
                        <span class="schedule-count" v-if="payment.schedule.length > 0">
                          {{ payment.schedule.length }}
                        </span>
                      </span>
                      <button type="button" class="btn btn-sm btn-gray btn-icon btn-icon-left"
                              v-if="scheduleLastYear" @click="copyScheduleYear">
                        <fa-icon :icon="['fal', 'copy']"/>
                        Copiar {{ scheduleLastYear }} para {{ scheduleLastYear + 1 }}
                      </button>
                      <button type="button"
                              class="btn btn-sm btn-secondary btn-icon btn-icon-left"
                              v-if="payment.schedule.length > 0"
                              @click="addScheduleCycle">
                        <fa-icon :icon="['fal', 'plus']"/>Adicionar ciclo
                      </button>
                    </div>
                    <table class="table payment-schedule-table"
                           v-if="payment.schedule.length > 0">
                      <thead>
                      <tr>
                        <th>
                          Corte de envio
                          <small>último dia para enviar</small>
                        </th>
                        <th>
                          Prévia
                          <small>opcional</small>
                        </th>
                        <th>
                          Prazo da NF
                          <small>opcional</small>
                        </th>
                        <th>
                          Crédito
                          <small>quando a operadora paga</small>
                        </th>
                        <th>
                          Repasse
                          <small>previsão para o profissional</small>
                        </th>
                        <th class="text-right">Ações</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(cycle, index) in payment.schedule" :key="index">
                        <td :class="{'has-error': cycleValidator(index).cutoffDate.$error}">
                          <dx-input-date class="form-input" placeholder="00/00/0000"
                                         v-model="cycle.cutoffDate"
                                         @blur="cycleValidator(index).cutoffDate.$touch()"/>
                        </td>
                        <td :class="{'has-error': cycleValidator(index).previewDate.$error}">
                          <dx-input-date class="form-input" placeholder="00/00/0000"
                                         v-model="cycle.previewDate"
                                         @blur="cycleValidator(index).previewDate.$touch()"/>
                        </td>
                        <td :class="{'has-error': cycleValidator(index)
                        .invoiceDeadline.$error}">
                          <dx-input-date class="form-input" placeholder="00/00/0000"
                                         v-model="cycle.invoiceDeadline"
                                         @blur="cycleValidator(index)
                                         .invoiceDeadline.$touch()"/>
                        </td>
                        <td :class="{'has-error': cycleValidator(index).paymentDate.$error}">
                          <dx-input-date class="form-input" placeholder="00/00/0000"
                                         v-model="cycle.paymentDate"
                                         @blur="cycleValidator(index).paymentDate.$touch()"/>
                        </td>
                        <td :class="{'has-error': cycleValidator(index).transferDate.$error}">
                          <dx-input-date class="form-input" placeholder="00/00/0000"
                                         v-model="cycle.transferDate"
                                         @blur="cycleValidator(index).transferDate.$touch()"/>
                        </td>
                        <td class="text-right">
                          <button type="button"
                                  class="btn btn-sm btn-gray btn-action btn-icon"
                                  data-tooltip="Remover ciclo"
                                  @click="removeScheduleCycle(index)">
                            <fa-icon :icon="['fal', 'trash']"/>
                          </button>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                    <div class="payment-schedule-empty"
                         :class="{'is-error': $v.form.settings.general.billing
                         .payment.schedule.$error}" v-else>
                      <span class="empty-icon">
                        <fa-icon :icon="['fal', 'calendar-alt']"/>
                      </span>
                      <div class="empty-text">
                        <strong v-if="$v.form.settings.general.billing.payment.schedule
                          .$error">
                          Adicione ao menos um ciclo
                        </strong>
                        <strong v-else>Nenhum ciclo cadastrado</strong>
                        <small>
                          Copie do calendário da operadora: o lote enviado até o corte entra
                          no ciclo; quem perde o corte cai no seguinte
                        </small>
                      </div>
                      <button type="button"
                              class="btn btn-sm btn-secondary btn-icon btn-icon-left"
                              @click="addScheduleCycle">
                        <fa-icon :icon="['fal', 'plus']"/>Adicionar ciclo
                      </button>
                    </div>
                  </div>

                  <div class="columns rule-extra">
                    <div class="column col-4 col-md-6 col-sm-12 form-group">
                      <label class="form-label" for="plan-non-business-day">
                        Se a data cair no fim de semana
                      </label>
                      <select id="plan-non-business-day" class="form-select"
                              v-model="payment.nonBusinessDay">
                        <option value="postpone">Adiar para segunda-feira</option>
                        <option value="anticipate">Antecipar para sexta-feira</option>
                      </select>
                    </div>
                  </div>

                  <div class="ip-summary">
                    <fa-icon :icon="['fal', 'info-circle']"/>
                    <span>{{ paymentSummary }}</span>
                  </div>
                </div>
              </section>

              <section class="ip-section">
                <header class="ip-section-head">
                  <span class="ip-section-icon">
                    <fa-icon :icon="['fal', 'file-invoice-dollar']"/>
                  </span>
                  <span class="ip-section-text">
                    <strong>Nota fiscal</strong>
                    <small>Quando a operadora exige a NF antes de liberar o crédito</small>
                  </span>
                </header>
                <div class="ip-section-body">
                  <div class="ip-switches">
                    <label class="ip-switch-row" for="sw-invoice-required">
                      <span class="ip-switch-text">
                        <strong>NF obrigatória antes do pagamento</strong>
                        <small>O crédito só é previsto depois que a nota é registrada</small>
                      </span>
                      <span class="ip-switch-field" v-if="payment.invoice.required"
                            :class="{'has-error': $v.form.settings.general.billing
                            .payment.invoice.deadlineDays.$error}">
                        <span class="input-group">
                          <dx-input-number
                            id="plan-invoice-deadline"
                            class="form-input text-center"
                            maxlength="3"
                            placeholder="—"
                            @blur="$v.form.settings.general.billing.payment
                            .invoice.deadlineDays.$touch()"
                            v-model="payment.invoice.deadlineDays"
                          />
                          <span class="input-group-addon">dias</span>
                        </span>
                      </span>
                      <span class="form-switch no-label">
                        <input type="checkbox" id="sw-invoice-required"
                               v-model="payment.invoice.required">
                        <i class="form-icon"></i>
                      </span>
                    </label>
                  </div>
                  <div class="form-input-hint" v-if="payment.invoice.required">
                    {{ invoiceDeadlineHint }}
                  </div>
                </div>
              </section>

              <section class="ip-section">
                <header class="ip-section-head">
                  <span class="ip-section-icon"><fa-icon :icon="['fal', 'gavel']"/></span>
                  <span class="ip-section-text">
                    <strong>Recurso de glosa</strong>
                    <small>Prazo do contrato para contestar o que a operadora glosou</small>
                  </span>
                </header>
                <div class="ip-section-body">
                  <div class="columns">
                    <div class="column col-12 form-group ip-inline-field"
                         :class="{'has-error': $v.form.settings.general
                         .billing.appealDays.$error}">
                      <label class="form-label" for="plan-appeal-days">
                        Prazo de recurso
                      </label>
                      <div class="input-group">
                        <dx-input-number
                          id="plan-appeal-days"
                          class="form-input text-center"
                          maxlength="3"
                          placeholder="—"
                          @blur="$v.form.settings.general.billing.appealDays.$touch()"
                          v-model="form.settings.general.billing.appealDays"
                        />
                        <span class="input-group-addon">dias</span>
                      </div>
                      <div class="form-input-hint"
                           v-if="$v.form.settings.general.billing.appealDays.$error">
                        Informe entre 1 e 365 dias
                      </div>
                      <div class="form-input-hint" v-else>
                        Contados a partir do protocolo do lote
                      </div>
                    </div>
                  </div>
                  <div class="ip-switches">
                    <label class="ip-switch-row" for="sw-allow-second-appeal">
                      <span class="ip-switch-text">
                        <strong>Permitir segunda rodada de recurso</strong>
                        <small>Recursar novamente a glosa mantida pela operadora</small>
                      </span>
                      <span class="form-switch no-label">
                        <input type="checkbox" id="sw-allow-second-appeal"
                               v-model="form.settings.general.billing.allowSecondAppeal">
                        <i class="form-icon"></i>
                      </span>
                    </label>
                  </div>
                </div>
              </section>
            </template>
          </st-tab>

          <st-tab id="tab-financial" name="Financeiro"
                  :badge="sectionErrors('tab-financial')" badge-type="alert">
            <section class="ip-section">
              <header class="ip-section-head">
                <span class="ip-section-icon"><fa-icon :icon="['fal', 'coins']"/></span>
                <span class="ip-section-text">
                  <strong>Lançamento do recebimento</strong>
                  <small>Para onde vai o dinheiro recebido deste plano</small>
                </span>
              </header>
              <div class="ip-section-body">
                <div class="columns">
                  <div class="column col-4 col-md-12 col-sm-12 form-group">
                    <label class="form-label" for="plan-method">Forma de pagamento</label>
                    <select id="plan-method" class="form-select"
                            v-model="form.settings.general.financial.methodId">
                      <option value="">[Selecione]</option>
                      <option v-for="(item, i) in methods"
                              :value="item.id" :key="i">{{ item.name }}</option>
                    </select>
                  </div>
                  <div class="column col-4 col-md-12 col-sm-12 form-group">
                    <label class="form-label" for="plan-category">Categoria</label>
                    <select id="plan-category" class="form-select"
                            v-model="form.settings.general.financial.categoryId">
                      <option value="">[Selecione]</option>
                      <option v-for="(item, i) in categories"
                              :value="item.id" :key="i">
                        {{ item?.parent?.name }} -> {{ item?.name }}
                      </option>
                    </select>
                  </div>
                  <div class="column col-4 col-md-12 col-sm-12 form-group">
                    <label class="form-label" for="plan-bank-account">
                      Conta financeira
                    </label>
                    <select id="plan-bank-account" class="form-select"
                            v-model="form.settings.general.financial.bankAccountId">
                      <option value="">[Selecione]</option>
                      <option v-for="(item, i) in bankAccounts"
                              :value="item.id" :key="i">{{ item.name }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </section>

            <section class="ip-section">
              <header class="ip-section-head">
                <span class="ip-section-icon"><fa-icon :icon="['fal', 'balance-scale']"/></span>
                <span class="ip-section-text">
                  <strong>Impostos e descontos</strong>
                  <small>Percentuais usados no cálculo do repasse deste plano</small>
                </span>
              </header>
              <div class="ip-section-body">
                <div class="columns">
                  <div class="column col-3 col-md-4 col-sm-6 form-group ip-pct"
                       v-for="tax in taxFields" :key="tax.key">
                    <label class="form-label" :for="`plan-tax-${tax.key}`">{{ tax.text }}</label>
                    <div class="input-group">
                      <dx-input-number :id="`plan-tax-${tax.key}`" class="form-input"
                                       v-model="form.settings.general.taxes[tax.key]"
                                       :precision="2"
                      />
                      <span class="input-group-addon">%</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </st-tab>

          <st-tab id="tab-professionals" name="Profissionais">
            <insurance-professionals
              v-if="!loading"
              :is-new="!form.id"
              :insurance-id="form.insurance.id"
              :plan-id="form.id"
            />
          </st-tab>

          <st-tab id="tab-expenses" name="Serviços e preços">
            <section class="ip-section">
              <header class="ip-section-head">
                <span class="ip-section-icon"><fa-icon :icon="['fal', 'table']"/></span>
                <span class="ip-section-text">
                  <strong>Tabelas de preços</strong>
                  <small>De onde saem os valores dos serviços deste plano</small>
                </span>
                <span class="ip-section-aside" v-if="!hasTables">
                  Vincule as quatro tabelas
                </span>
              </header>
              <div class="ip-section-body">
                <div class="columns">
                  <div class="column col-6 col-md-12 col-sm-12 form-group"
                       v-for="table in tableFields" :key="table.key">
                    <label class="form-label" :for="`plan-table-${table.key}`">
                      {{ table.text }}
                    </label>
                    <div class="input-group">
                      <select :id="`plan-table-${table.key}`" class="form-select"
                              :disabled="loadingTables"
                              v-model="form.settings.general.table[table.key]">
                        <option :value="null">[Selecione]</option>
                        <option v-for="(item, i) in tables"
                                :value="item.id" :key="i">{{ item.name }}</option>
                      </select>
                      <router-link
                        class="btn btn-gray input-group-btn"
                        :to="getTableUrl(form.settings.general.table[table.key])"
                        :disabled="!form.settings.general.table[table.key]"
                        target="_blank"
                        rel="noopener noreferrer">
                        Gerenciar
                      </router-link>
                    </div>
                  </div>
                </div>
                <div class="ip-table-actions">
                  <button type="button"
                          class="btn btn-icon btn-icon-left btn-gray mr-2"
                          :class="{loading: loadingTables}"
                          :disabled="loadingTables"
                          @click="loadTables">
                    <fa-icon :icon="['fal', 'sync']" />Recarregar tabelas
                  </button>
                  <router-link
                    class="btn btn-secondary btn-icon btn-icon-left mr-2"
                    to="/clinical/expense-tables"
                    target="_blank"
                    rel="noopener noreferrer">
                    <fa-icon :icon="['fal', 'pencil']" />Gerenciar tabelas
                  </router-link>
                  <button type="button"
                          class="btn btn-icon btn-icon-left btn-warning tooltip tooltip-left"
                          :class="{loading: savingTables}"
                          :data-tooltip="updateExpensesHint"
                          :disabled="savingTables || !canUpdateExpenses"
                          @click="updateExpenses">
                    <fa-icon :icon="['fal', 'sack-dollar']" />Atualizar valores
                  </button>
                </div>
              </div>
            </section>

            <section class="ip-section">
              <header class="ip-section-head">
                <span class="ip-section-icon"><fa-icon :icon="['fal', 'list']"/></span>
                <span class="ip-section-text">
                  <strong>Serviços, taxas e outras despesas</strong>
                  <small>
                    Para alterar regras e valores, edite a tabela vinculada ao plano
                  </small>
                </span>
                <span class="ip-section-aside" v-if="expenses.length > 0">
                  {{ filteredExpenses.length }} de {{ expenses.length }}
                </span>
              </header>
              <div class="ip-section-body">
                <div class="empty" v-if="expenses.length === 0">
                  <div class="empty-icon">
                    <fa-icon :icon="['fal', 'sack-dollar']" size="3x"/>
                  </div>
                  <p class="empty-title h5">Nenhum serviço vinculado</p>
                  <p class="empty-subtitle">
                    Vincule as tabelas de preços acima e clique em <strong>Atualizar
                    valores</strong> para trazer os serviços com os valores deste plano.
                  </p>
                </div>
                <template v-else>
                  <div class="columns">
                    <div class="column col-4 col-md-6 col-sm-12 form-group">
                      <label class="form-label" for="expense-type">Filtrar tipo</label>
                      <select id="expense-type" class="form-select" v-model="filter.type">
                        <option value="">Todos</option>
                        <option v-for="(text, value) in expenseTypes"
                                :value="value" :key="value">{{ text }}</option>
                      </select>
                    </div>
                    <div class="column col-8 col-md-6 col-sm-12 form-group">
                      <label class="form-label" for="expense-search">Pesquisar</label>
                      <div class="input-group">
                        <input
                          type="text"
                          id="expense-search"
                          class="form-input"
                          v-model="filter.search"
                          placeholder="Pesquisar por nome do serviço">
                        <button type="button"
                                class="btn btn-neutral btn-action input-group-btn btn-icon"
                                tabindex="-1">
                          <fa-icon :icon="['fal', 'search']" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="empty" v-if="filteredExpenses.length === 0">
                    <div class="empty-icon">
                      <fa-icon :icon="['fal', 'search']" size="3x"/>
                    </div>
                    <p class="empty-title h5">Nenhum serviço encontrado</p>
                    <p class="empty-subtitle">Nenhum serviço atende a este filtro.</p>
                    <button type="button" class="btn btn-secondary" @click="clearExpenseFilter">
                      Limpar filtro
                    </button>
                  </div>
                  <table class="table table-hover" v-else>
                    <thead>
                    <tr>
                      <th class="hide-md hide-sm" style="width: 40px">#</th>
                      <th class="hide-md hide-sm" style="width: 90px">Código</th>
                      <th>Descrição</th>
                      <th class="hide-md hide-sm">Tabela de preço</th>
                      <th class="text-right" style="width: 120px">Valor plano</th>
                      <th class="text-right" style="width: 120px">Valor paciente</th>
                      <th class="text-right" style="width: 80px">Repasse</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item, i) in filteredExpenses" :key="i">
                      <td class="text-center hide-md hide-sm">{{ i + 1 }}</td>
                      <td class="text-center hide-md hide-sm">{{ item.code | tuss }}</td>
                      <td>{{ item.expense.name }}</td>
                      <td>{{ item.table.name }}</td>
                      <td class="text-right">
                        {{ item.definition.insuranceValue | currency }}
                      </td>
                      <td class="text-right">
                        {{ item.definition.particularValue | currency }}
                      </td>
                      <td class="text-right">
                        <span v-if="item.definition.transferType === 'value'">
                          {{ item.definition.transferValue | currency }}
                        </span>
                        <span v-else>{{ item.definition.transferValue | number }}%</span>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </template>
              </div>
            </section>
          </st-tab>
        </st-tabs>
      </div>
      <div class="card-footer ip-footer">
        <button class="btn btn-primary btn-icon btn-icon-left"
                @click="save" :disabled="saving"
                :class="{loading: saving}">
          <fa-icon :icon="['fal', 'save']" />Salvar
        </button>
        <button class="btn btn-gray-outline"
                @click="$router.push('/clinical/insurance-plans')">
          Voltar
        </button>
        <span class="ip-footer-state" v-if="submitted && totalErrors > 0">
          <fa-icon :icon="['fal', 'exclamation-circle']"/>
          {{ totalErrors }}
          {{ totalErrors === 1 ? 'campo precisa' : 'campos precisam' }} de correção
        </span>
        <button class="btn btn-icon btn-icon-left btn-secondary ip-footer-schedule"
                v-if="form.id && $can(UPDATE_SCHEDULE)"
                @click="scheduleConfigModalShow = true">
          <fa-icon :icon="['fal', 'cog']" />
          Gerenciar plano nas agendas
        </button>
      </div>
    </div>

    <table-conflicts-modal
      v-if="conflictsModal.show"
      :items="conflictsModal.items"
      @confirm="closeTableConflicts"
      @cancel="closeTableConflicts(false)"
    />

    <insurance-modal
      v-if="insuranceModal.show"
      :is-new="insuranceModal.isNew"
      @save="saveInsurance"
      @remove="removeInsurance"
      @close="closeInsuranceModal"
    />

    <schedule-config-modal
      v-if="form.id && scheduleConfigModalShow"
      type="insurance"
      :id="form.id"
      :insurance-id="form.insurance.id"
      :name="form.name"
      @close="scheduleConfigModalShow = false"
    />

  </div>
</template>

<script>
import { customName } from 'src/helpers/insurance';
import { tissTags } from '@/data/tiss-tags';
import { types as expenseTypes } from 'src/data/expense-types';
import { types as insuranceTypes } from 'src/data/insurance-types';
import { tissVersions } from '@/data/tiss-versions';
import moment from 'moment';
import {
  minValue, maxValue, required, requiredIf,
} from 'vuelidate/lib/validators';
import { cnpj, date } from 'src/data/validators';
import { mergeFrom } from '@/helpers/object';
import fuzzy from 'fuzzy';
import { mapState } from 'vuex';
import { UPDATE_SCHEDULE } from '@/data/actions/modules/clinical';
import ScheduleConfigModal from '../../schedule/components/modals/Config.vue';
import formMixin from '../../../mixins/form';
import InsuranceModal from './modals/InsuranceModal.vue';
import InsuranceProfessionals from './modals/InsuranceProfessionals.vue';
import TableConflictsModal from './modals/TableConflictsModal.vue';

const DEFAULT_SUBMISSION_DAYS = 90;

export default {
  mixins: [formMixin],
  components: {
    InsuranceModal,
    TableConflictsModal,
    InsuranceProfessionals,
    ScheduleConfigModal,
  },
  data() {
    return {
      UPDATE_SCHEDULE,
      isNew: false,
      loadedLastValue: null,
      loadedTableIds: '',
      loading: false,
      loadingBranch: false,
      loadingTables: false,
      saving: false,
      savingTables: false,
      expenseTypes,
      insuranceTypes,
      tissVersions,
      tissTags,
      customName,
      bankAccounts: [],
      categories: [],
      expenses: [],
      tables: [],
      methods: [],
      insurances: [],
      scheduleConfigModalShow: false,
      filter: {
        type: '',
        search: '',
      },
      conflictsModal: {
        show: false,
        items: [],
        resolve: null,
      },
      insuranceModal: {
        show: false,
        isNew: false,
      },
      submitted: false,
      sections: [
        { id: 'tab-data', name: 'Dados principais' },
        { id: 'tab-attendance', name: 'Agenda e atendimento' },
        { id: 'tab-tiss', name: 'Guias TISS' },
        { id: 'tab-billing', name: 'Faturamento' },
        { id: 'tab-financial', name: 'Financeiro' },
        { id: 'tab-professionals', name: 'Profissionais' },
        { id: 'tab-expenses', name: 'Serviços e preços' },
      ],
      taxFields: [
        { key: 'iss', text: 'ISS' },
        { key: 'irpj', text: 'IRPJ' },
        { key: 'pis', text: 'PIS' },
        { key: 'csll', text: 'CSLL' },
        { key: 'cofins', text: 'COFINS' },
        { key: 'epp', text: 'ME/EPP' },
        { key: 'admin', text: 'Taxa administrativa' },
        { key: 'others', text: 'Outros' },
      ],
      tableFields: [
        { key: 'procedureId', text: 'Tabela de preço dos procedimentos' },
        { key: 'examId', text: 'Tabela de preço dos exames' },
        { key: 'materialId', text: 'Tabela de preço dos materiais' },
        { key: 'medicineId', text: 'Tabela de preço dos medicamentos' },
      ],
      paymentRuleTypes: [
        {
          value: 'daysAfter',
          text: 'Dias após um marco',
          icon: 'clock',
          description: 'X dias depois do envio, fechamento, protocolo ou NF',
        },
        {
          value: 'fixedDays',
          text: 'Dias fixos do mês',
          icon: 'calendar-day',
          description: 'A operadora paga sempre nos mesmos dias do mês',
        },
        {
          value: 'schedule',
          text: 'Cronograma anual',
          icon: 'calendar-alt',
          description: 'Calendário da operadora, com corte de envio e crédito',
        },
      ],
      paymentMilestones: [
        { value: 'closed', text: 'Fechamento do lote', summary: 'o fechamento do lote' },
        { value: 'sent', text: 'Envio à operadora', summary: 'o envio à operadora' },
        { value: 'protocol', text: 'Protocolo da operadora', summary: 'o protocolo da operadora' },
        { value: 'invoice', text: 'Emissão da nota fiscal', summary: 'a emissão da nota fiscal' },
      ],
      reducers: [
        { name: 'Primeiro procedimento', value: 100, enabled: false },
        { name: 'Segundo procedimento', value: 70, enabled: true },
        { name: 'Terceiro procedimento', value: 50, enabled: true },
        { name: 'Demais procedimentos', value: 50, enabled: true },
      ],
      form: this.blankForm(),
    };
  },
  async mounted() {
    await this.init();
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    isInsurance() {
      return this.form.type === 'insurance';
    },
    isCompany() {
      return this.form.settings.tiss.contract.type === 'company';
    },
    hasTables() {
      return this.form.settings.general.table.procedureId
        && this.form.settings.general.table.examId
        && this.form.settings.general.table.materialId
        && this.form.settings.general.table.medicineId;
    },
    recordDigitsActive() {
      return this.form.settings.general.record.digits.enabled;
    },
    billingAccountsActive() {
      return this.form.settings.general.billing.accounts.enabled;
    },
    payment() {
      return this.form.settings.general.billing.payment;
    },
    providerReferralMode() {
      return this.form.settings.general.providerReferral.mode;
    },
    planSummary() {
      if (!this.isInsurance) {
        return 'Plano sem convênio: as guias e os prazos de operadora não se aplicam.';
      }
      if (!this.form.settings.tiss.enabled) {
        return 'As guias deste plano são genéricas: o sistema não gera o XML TISS, '
          + 'mas os prazos de lote e de glosa continuam valendo.';
      }
      const version = this.tissVersions[this.form.settings.tiss.version];
      return version
        ? `As guias seguem o padrão TISS ${version} e o lote gera XML para a operadora.`
        : 'Escolha a versão TISS que a operadora aceita para gerar o XML do lote.';
    },
    returnDaysHint() {
      const days = Number(this.form.returnDays) || 0;
      if (!days) {
        return 'Sem prazo: todo atendimento é cobrado como novo, nunca como retorno.';
      }
      return `O mesmo serviço repetido em até ${days} ${days === 1 ? 'dia' : 'dias'} `
        + 'entra como retorno, sem nova cobrança.';
    },
    canUpdateExpenses() {
      return !!this.form.id && this.hasTables;
    },
    updateExpensesHint() {
      if (!this.form.id) return 'Salve o plano antes de atualizar os valores';
      if (!this.hasTables) return 'Vincule as quatro tabelas de preço';
      return 'Traz os serviços das tabelas com os valores deste plano';
    },
    formFields() {
      const { general, tiss } = this.$v.form.settings;
      const { payment } = general.billing;
      const field = (section, label, id, validator) => ({
        section, label, id, validator,
      });
      return [
        field('tab-data', 'Operadora', 'plan-insurance', this.$v.form.insurance.id),
        field('tab-data', 'Nome do plano', 'plan-name', this.$v.form.name),
        field('tab-data', 'Tipo', 'plan-type', this.$v.form.type),
        field('tab-data', 'Registro ANS', 'plan-record', this.$v.form.record),
        field('tab-data', 'Versão TISS', 'version', tiss.version),
        field('tab-attendance', 'Dias de retorno', 'plan-return-days', this.$v.form.returnDays),
        field('tab-attendance', 'Dígitos da carteirinha (mínimo)', 'record-digits-min', general.record.digits.min),
        field('tab-attendance', 'Dígitos da carteirinha (máximo)', 'record-digits-max', general.record.digits.max),
        field('tab-tiss', 'Código na operadora', 'operator', tiss.contract.code),
        field('tab-tiss', 'CNPJ do contrato', 'identity', tiss.contract.identity),
        field('tab-tiss', 'Nome do contrato', 'name', tiss.contract.name),
        field('tab-tiss', 'CNES', 'cnes', tiss.contract.cnes),
        field('tab-tiss', 'Dias da validade da senha', 'tiss-validity-days', tiss.sadt.authorization.fillValidityDate.days),
        field('tab-billing', 'Guia atual', 'provider-last-value', general.providerReferral.lastValue),
        field('tab-billing', 'Prazo de apresentação', 'plan-submission-days', general.billing.submissionDays),
        field('tab-billing', 'Guias por lote', 'plan-accounts-quantity', general.billing.accounts.quantity),
        field('tab-billing', 'Dias do pagamento', 'plan-payment-days', payment.daysAfter.days),
        field('tab-billing', 'Dias do mês em que a operadora paga', 'plan-fixed-days', payment.fixedDays.days),
        field('tab-billing', 'Carência', 'plan-payment-grace', payment.fixedDays.graceDays),
        field('tab-billing', 'Ciclos do cronograma', 'plan-schedule', payment.schedule),
        field('tab-billing', 'Prazo da NF', 'plan-invoice-deadline', payment.invoice.deadlineDays),
        field('tab-billing', 'Prazo de recurso', 'plan-appeal-days', general.billing.appealDays),
      ];
    },
    invalidFields() {
      return this.formFields.filter(item => item.validator && item.validator.$error);
    },
    sectionErrorMap() {
      return this.invalidFields.reduce((result, item) => ({
        ...result,
        [item.section]: (result[item.section] || 0) + 1,
      }), {});
    },
    totalErrors() {
      return this.invalidFields.length;
    },
    invalidSections() {
      return this.sections.filter(section => this.sectionErrorMap[section.id] > 0);
    },
    submissionDaysHint() {
      return this.$v.form.settings.general.billing.submissionDays.required === false
        ? 'Campo obrigatório'
        : 'Informe entre 1 e 365 dias';
    },
    paymentRuleSummary() {
      const { payment } = this;
      if (payment.type === 'fixedDays') {
        const days = [...payment.fixedDays.days].sort((a, b) => a - b);
        if (days.length === 0) return 'Selecione os dias do mês em que a operadora paga.';
        const list = days.length === 1
          ? `${days[0]}`
          : `${days.slice(0, -1).join(', ')} ou ${days[days.length - 1]}`;
        const grace = Number(payment.fixedDays.graceDays) || 0;
        const after = grace > 0
          ? `${grace} ${grace === 1 ? 'dia' : 'dias'} depois do envio`
          : 'depois do envio do lote';
        return `Pagamento no primeiro dia ${list} que vier ${after}.`;
      }
      if (payment.type === 'schedule') {
        const cycles = payment.schedule.length;
        if (cycles === 0) return 'Cadastre os ciclos do calendário para ter previsão de pagamento.';
        const cutoffs = payment.schedule
          .map(cycle => moment(cycle.cutoffDate, 'YYYY-MM-DD', true))
          .filter(value => value.isValid())
          .sort((a, b) => a.valueOf() - b.valueOf());
        const total = `${cycles} ${cycles === 1 ? 'ciclo cadastrado' : 'ciclos cadastrados'}`;
        const range = cutoffs.length > 1
          ? `, com cortes de ${cutoffs[0].format('DD/MM/YYYY')} `
            + `a ${cutoffs[cutoffs.length - 1].format('DD/MM/YYYY')}`
          : '';
        return `${total}${range}. O lote entra no primeiro ciclo cujo corte ainda não passou.`;
      }
      const milestone = this.paymentMilestones
        .find(option => option.value === payment.daysAfter.from);
      const from = milestone ? milestone.summary : 'o envio à operadora';
      const days = Number(payment.daysAfter.days) || 0;
      if (!days) return `Sem prazo informado: vale o padrão de dias da filial, contado após ${from}.`;
      return `A operadora paga ${days} ${days === 1 ? 'dia' : 'dias'} após ${from}.`;
    },
    paymentSummary() {
      const weekend = this.payment.nonBusinessDay === 'anticipate'
        ? 'Se cair no fim de semana, antecipa para sexta-feira.'
        : 'Se cair no fim de semana, adia para segunda-feira.';
      return `${this.paymentRuleSummary} ${weekend}`;
    },
    invoiceDeadlineHint() {
      if (this.payment.type === 'schedule') {
        return 'Dias após a prévia, quando o ciclo não tem prazo da NF';
      }
      return 'Dias após o protocolo (ou o envio) para emitir a NF';
    },
    fixedDaysSummary() {
      const total = this.payment.fixedDays.days.length;
      if (total === 0) return 'Clique nos dias em que a operadora costuma pagar';
      return `${total} ${total === 1 ? 'data' : 'datas'} de pagamento por mês`;
    },
    scheduleLastYear() {
      const years = this.payment.schedule
        .map(cycle => moment(cycle.cutoffDate, 'YYYY-MM-DD', true))
        .filter(value => value.isValid())
        .map(value => value.year());
      return years.length > 0 ? Math.max(...years) : null;
    },
    fillValidityDateActive() {
      return this.form.settings.tiss.sadt.authorization.fillValidityDate.enabled;
    },
    filteredExpenses() {
      let { expenses } = this;

      if (this.filter.type) {
        expenses = expenses.filter(item => item.expense.type === this.filter.type);
      }

      const search = this.filter.search.trim();

      if (search) {
        const results = fuzzy.filter(search, expenses, {
          extract: (el) => {
            if (el.expense.name) {
              return this.removeAccentuation(el.expense.name);
            }
            return '';
          },
        });
        expenses = results.map(({ original }) => original);
      }

      return expenses;
    },
    providerReferralActive() {
      return this.form.settings.general.providerReferral.mode === 'automatic';
    },
    getTitle() {
      if (!this.form.id) {
        return 'Novo Registro';
      }
      return customName({
        id: this.form.insurance.id,
        name: this.form.insurance.name,
        plan: {
          id: this.form.id,
          name: this.form.name,
        },
      });
    },
    referralOrigin: {
      get() {
        return this.encodeTypeValue(this.form.settings.tiss.referral.origin);
      },
      set(value) {
        this.form.settings.tiss.referral.origin = this.decodeTypeValue(value);
      },
    },
    referralRequester: {
      get() {
        return this.encodeTypeValue(this.form.settings.tiss.referral.requester);
      },
      set(value) {
        this.form.settings.tiss.referral.requester = this.decodeTypeValue(value);
      },
    },
    referralExecutant: {
      get() {
        return this.encodeTypeValue(this.form.settings.tiss.referral.executant);
      },
      set(value) {
        this.form.settings.tiss.referral.executant = this.decodeTypeValue(value);
      },
    },
    referralProfessional: {
      get() {
        return this.encodeTypeValue(this.form.settings.tiss.referral.professional);
      },
      set(value) {
        this.form.settings.tiss.referral.professional = this.decodeTypeValue(value);
      },
    },
  },
  validations() {
    const rules = {
      form: {
        type: { required },
        name: { required },
        record: {},
        insurance: {
          id: { required },
        },
        returnDays: {},
        settings: {
          general: {
            record: {
              digits: {
                min: {},
                max: {},
              },
            },
            billing: {
              accounts: {
                quantity: {},
              },
              payment: {
                daysAfter: {
                  days: {
                    minValue: minValue(1),
                    maxValue: maxValue(365),
                  },
                },
                fixedDays: {
                  days: {
                    required: requiredIf(() => this.isInsurance && this.payment.type === 'fixedDays'),
                  },
                  graceDays: {
                    minValue: minValue(0),
                    maxValue: maxValue(365),
                  },
                },
                schedule: {
                  required: requiredIf(() => this.isInsurance && this.payment.type === 'schedule'),
                  $each: {
                    cutoffDate: {
                      required: requiredIf(() => this.isInsurance && this.payment.type === 'schedule'),
                      date,
                    },
                    previewDate: { date },
                    invoiceDeadline: { date },
                    transferDate: { date },
                    paymentDate: {
                      required: requiredIf(() => this.isInsurance && this.payment.type === 'schedule'),
                      date,
                      afterCutoff: (value, cycle) => !value || !cycle.cutoffDate
                        || !moment(value, 'YYYY-MM-DD', true).isValid()
                        || !moment(cycle.cutoffDate, 'YYYY-MM-DD', true).isValid()
                        || value >= cycle.cutoffDate,
                    },
                  },
                },
                invoice: {
                  deadlineDays: {
                    minValue: minValue(1),
                    maxValue: maxValue(365),
                  },
                },
              },
              submissionDays: {
                required,
                minValue: minValue(1),
                maxValue: maxValue(365),
              },
              appealDays: {
                minValue: minValue(1),
                maxValue: maxValue(365),
              },
            },
            providerReferral: {
              lastValue: {},
            },
          },
          tiss: {
            version: {},
            xml: {
              batch: {},
            },
            contract: {
              code: {},
              identity: {},
              name: {},
              cnes: {},
            },
            sadt: {
              authorization: {
                fillValidityDate: {
                  days: {},
                },
              },
            },
          },
        },
      },
    };

    if (this.isInsurance) {
      rules.form.record = { required };

      if (this.form.settings.tiss.enabled) {
        rules.form.settings.tiss.version = { required };
      }
    }

    const useTiss = !!this.form.settings.tiss.enabled;

    if (useTiss) {
      rules.form.settings.tiss.contract.cnes = { required };
      rules.form.settings.tiss.contract.identity = { cnpj };
    }

    if (this.form.settings.general.record.digits.enabled) {
      rules.form.settings.general.record.digits.min = {
        required,
        minValue: minValue(1),
      };
      rules.form.settings.general.record.digits.max = {
        required,
        minValue: minValue(1),
      };
      if (this.form.settings.general.record.digits.min > 0) {
        rules.form.settings.general.record.digits.max = {
          required,
          minValue: minValue(this.form.settings.general.record.digits.min),
        };
      }
    }

    if (useTiss && this.form.settings.general.billing.accounts.enabled) {
      rules.form.settings.general.billing.accounts.quantity = {
        required,
        minValue: minValue(1),
      };
    }

    if (useTiss && this.form.settings.general.providerReferral.mode === 'automatic') {
      rules.form.settings.general.providerReferral.lastValue = {
        required,
        minValue: minValue(0),
      };
    }

    if (useTiss && !this.form.settings.tiss.version) {
      rules.form.settings.tiss.version = { required };
    }

    if (useTiss && this.form.settings.tiss.contract.type === 'company') {
      if (!this.form.settings.tiss.contract.code) {
        rules.form.settings.tiss.contract.code = { required };
      }
      if (!this.form.settings.tiss.contract.identity) {
        rules.form.settings.tiss.contract.identity = { required };
      }
      if (!this.form.settings.tiss.contract.name) {
        rules.form.settings.tiss.contract.name = { required };
      }
    }

    if (useTiss
      && this.form.settings.tiss.sadt.authorization.fillValidityDate.enabled) {
      rules.form.settings.tiss.sadt.authorization.fillValidityDate.days = {
        required,
        minValue: minValue(1),
      };
    }

    return rules;
  },
  methods: {
    async init() {
      this.loading = true;

      this.isNew = /create$/.test(this.$route.path);
      this.loadBankAccounts();
      this.loadCategories();
      this.loadMethods();
      this.loadTables();
      this.loadInsurances();

      if (!this.isNew) {
        await this.load();
        this.loadExpenses();
      }

      if (!this.isNew) {
        this.$v.form.$touch();
      }

      this.loading = false;
    },
    load() {
      const { insuranceId } = this.$route.params;
      const planId = this.$route.params.id;

      return this.$http
        .get(`/insurances/${insuranceId}/plans/${planId}`)
        .then(({ data }) => {
          this.form = mergeFrom(this.blankForm(), data);
          const billing = (data.settings && data.settings.general && data.settings.general.billing)
            || {};
          this.form.settings.general.billing.payment = this.paymentForm(
            billing.payment,
            billing.paymentDays,
          );
          if (!this.form.settings.general.billing.submissionDays) {
            this.form.settings.general.billing.submissionDays = DEFAULT_SUBMISSION_DAYS;
          }
          this.loadedLastValue = this.form.settings.general.providerReferral.lastValue;
          this.loadedTableIds = this.planTableIds().join(',');
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro ao carregar o plano. Tente novamente!');
        });
    },
    loadBankAccounts() {
      const params = {
        active: true,
        limit: 0,
      };

      return this.$http
        .get('/bank-accounts', { params })
        .then(({ data }) => {
          this.bankAccounts = data?.items || [];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        });
    },
    loadCategories() {
      const params = {
        active: true,
        allowsValue: true,
        limit: 0,
      };

      return this.$http
        .get('/categories', { params })
        .then(({ data }) => {
          this.categories = data?.items || [];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        });
    },
    loadMethods() {
      const params = {
        active: true,
        limit: 0,
      };

      return this.$http
        .get('/payment-methods', { params })
        .then(({ data }) => {
          this.methods = data?.items || [];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        });
    },
    loadTables() {
      this.loadingTables = true;
      const params = {
        active: true,
        limit: 1000,
      };

      return this.$http
        .get('/expense-tables', { params })
        .then(({ data }) => {
          this.tables = data?.items || [];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        })
        .finally(() => {
          this.loadingTables = false;
        });
    },
    loadExpenses() {
      if (!this.form.insurance.id || !this.form.id) {
        return null;
      }

      const params = {
        insuranceId: this.form.insurance.id,
        planId: this.form.id,
        origin: 'plan',
        limit: 1000,
      };

      return this.$http
        .get('/expense-insurances', { params })
        .then(({ data }) => {
          this.expenses = data?.items || [];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        })
        .finally({});
    },
    loadInsurances() {
      const params = {
        active: true,
        limit: 0,
      };

      return this.$http.get('/insurances', { params })
        .then(({ data }) => {
          this.insurances = data?.items || [];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro. Tente novamente!');
        });
    },
    sectionErrors(id) {
      return this.sectionErrorMap[id] || null;
    },
    sectionName(id) {
      const section = this.sections.find(item => item.id === id);
      return section ? section.name : '';
    },
    goToSection(id) {
      this.$refs['plan-tabs'].selectTab(id);
      this.$nextTick(() => {
        const field = this.$el.querySelector('.tab-panel[aria-hidden="false"] .has-error');
        if (field) field.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    },
    goToField(item) {
      this.$refs['plan-tabs'].selectTab(item.section);
      this.$nextTick(() => {
        const element = document.getElementById(item.id);
        if (!element) {
          this.goToSection(item.section);
          return;
        }
        element.scrollIntoView({ block: 'center', behavior: 'smooth' });
        if (element.focus) element.focus({ preventScroll: true });
      });
    },
    clearExpenseFilter() {
      this.filter.type = '';
      this.filter.search = '';
    },
    async save() {
      this.submitted = true;
      this.$v.form.$touch();

      const [invalid] = this.invalidFields;

      if (invalid) {
        const total = this.totalErrors;
        this.$toast.error(total === 1
          ? `Revise "${invalid.label}" na aba ${this.sectionName(invalid.section)}`
          : `${total} campos precisam de correção. O primeiro está em `
            + `${this.sectionName(invalid.section)}`);
        this.goToField(invalid);
        return;
      }

      const tableIds = this.planTableIds().join(',');
      let resolution = {};

      if (tableIds !== this.loadedTableIds) {
        this.saving = true;
        const conflicts = await this.loadTableConflicts();
        this.saving = false;

        if (conflicts === null) {
          return;
        }

        resolution = conflicts.length > 0
          ? await this.showTableConflicts(conflicts)
          : {};
        if (!resolution) {
          return;
        }
      }

      this.saving = true;

      const data = this.clone(this.form);
      data.settings.general.billing.payment = this.paymentPayload();
      data.tableChoices = resolution.choices || [];
      data.removeDuplicates = !!resolution.removeDuplicates;
      const insuranceId = data.insurance.id;
      const planId = data.id;

      if (!data.companyName) {
        delete data.companyName;
      }

      if (!data?.identity?.value) {
        delete data.identity;
      }

      if (!data.record) {
        delete data.record;
      }

      const sentLastValue = data.settings.general.providerReferral.lastValue;

      if (!this.isNew && String(sentLastValue) === String(this.loadedLastValue)) {
        delete data.settings.general.providerReferral.lastValue;
      }

      const request = this.isNew
        ? this.$http.post(`/insurances/${insuranceId}/plans`, data)
        : this.$http.put(`/insurances/${insuranceId}/plans/${planId}`, data);

      // eslint-disable-next-line consistent-return
      return request
        .then(({ data: result }) => {
          if (this.isNew) {
            this.$router.replace(`/clinical/insurances/${insuranceId}/plans/${result.id}/edit`);
            this.isNew = false;
            this.form.id = result.id;
          }
          this.loadedLastValue = sentLastValue;
          this.loadedTableIds = tableIds;
          if (result.pricesUpdated) {
            const activeTableIds = this.tables.map(({ id }) => id);
            ['procedureId', 'examId', 'materialId', 'medicineId'].forEach((field) => {
              if (!activeTableIds.includes(this.form.settings.general.table[field])) {
                this.form.settings.general.table[field] = null;
              }
            });
            this.loadedTableIds = this.planTableIds().join(',');
            this.loadExpenses();
            this.$toast.show('Registro salvo! Preços dos serviços atualizados pelas tabelas.');
          } else {
            this.$toast.show('Registro salvo!');
          }
          return result;
        })
        .catch((e) => {
          const message = e.response && e.response.data && e.response.data.message;
          if (message) {
            this.$toast.error(message, { timeout: 6000 });
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
    openInsuranceModal() {
      this.insuranceModal.isNew = false;
      this.insuranceModal.show = true;
    },
    changeInsurance() {
      if (this.form.insurance.id === 'is_new') {
        this.insuranceModal.isNew = true;
        this.insuranceModal.show = true;
      }
    },
    async saveInsurance(insurance) {
      await this.loadInsurances();
      const found = this.insurances.find(item => item.id === insurance.id);

      if (found) {
        this.form.insurance = found;
      }
      this.insuranceModal.show = false;
    },
    closeInsuranceModal() {
      if (this.insuranceModal.isNew) {
        this.form.insurance = { id: '', name: '' };
      }
      this.insuranceModal.show = false;
    },
    removeInsurance(id) {
      this.form.insurance = { id: '', name: '' };
      const found = this.insurances.find(item => item.id === id);
      this.insurances.splice(this.insurances.indexOf(found), 1);
    },
    getCompanyData() {
      this.loadingBranch = true;

      return this.$http.get(`/branches/${this.user.branch.id}`)
        .then(({ data }) => {
          if (data.identity) {
            if (data.identity.type === 'cnpj') {
              this.form.settings.tiss.contract.code = data.identity.value;
              this.form.settings.tiss.contract.identity = data.identity.value;
            }
          }
          if (data.companyName) {
            this.form.settings.tiss.contract.name = data.companyName;
          }
          if (data.branch) {
            this.form.settings.tiss.contract.cnes = data.branch.cnes;
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loadingBranch = false;
        });
    },
    changeType() {
      if (this.form.type !== 'insurance') {
        this.form.settings.tiss.enabled = false;
        this.form.settings.tiss.version = '';
      }
    },
    decodeTypeValue(str) {
      const [type, value] = str.split('_');
      return { type, value };
    },
    encodeTypeValue({ type, value }) {
      return [type, value].join('_');
    },
    async updateExpenses() {
      if (!this.form.insurance.id || !this.form.id) {
        this.$toast.error('Primeiro precisa salvar este plano de saúde!');
        return;
      }
      if (!this.hasTables) {
        this.$toast.error('Você precisa vincular as tabelas para atualizar os preços!');
        return;
      }

      this.savingTables = true;
      const conflicts = await this.loadTableConflicts();
      this.savingTables = false;

      if (conflicts === null) {
        return;
      }

      if (conflicts.length > 0) {
        const resolution = await this.showTableConflicts(conflicts);
        if (resolution) {
          this.runUpdateExpenses(resolution);
        }
        return;
      }

      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + '<div>Deseja realmente atualizar os preços dos serviços?</div>'
          + '</div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              close();
              return this.runUpdateExpenses();
            },
          },
        ],
      });
    },
    runUpdateExpenses(resolution = {}) {
      const insuranceId = this.form.insurance.id;
      const planId = this.form.id;
      const data = {
        tableIds: this.planTableIds().map(id => id || null),
        choices: resolution.choices || [],
        removeDuplicates: !!resolution.removeDuplicates,
      };

      this.savingTables = true;

      return this.$http
        .put(`/insurances/${insuranceId}/plans/${planId}/update-expenses`, data)
        .then(async () => {
          const activeTableIds = this.tables.map(({ id }) => id);
          ['procedureId', 'examId', 'materialId', 'medicineId'].forEach((field) => {
            if (!activeTableIds.includes(this.form.settings.general.table[field])) {
              this.form.settings.general.table[field] = null;
            }
          });
          this.loadedTableIds = this.planTableIds().join(',');
          await this.loadExpenses();
          this.$refs['plan-tabs'].selectTab('tab-expenses');
          this.$toast.show('Valores dos planos atualizados com sucesso!');
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro ao atualizar os valores do plano!');
        })
        .finally(() => {
          this.savingTables = false;
        });
    },
    planTableIds() {
      const { table } = this.form.settings.general;
      return ['procedureId', 'examId', 'materialId', 'medicineId']
        .map(field => (table && table[field]) || '');
    },
    async loadTableConflicts() {
      const tableIds = this.planTableIds();

      if (tableIds.filter(Boolean).length < 2) {
        return [];
      }

      try {
        const params = { tableIds: tableIds.join(','), planId: this.form.id || undefined };
        const { data } = await this.$http.get('/expense-tables/conflicts', { params });
        return data.items || [];
      } catch (e) {
        this.$toast.error('Não foi possível verificar os serviços repetidos nas tabelas.');
        return null;
      }
    },
    showTableConflicts(conflicts) {
      return new Promise((resolve) => {
        this.conflictsModal = { show: true, items: conflicts, resolve };
      });
    },
    closeTableConflicts(result) {
      const { resolve } = this.conflictsModal;
      this.conflictsModal = { show: false, items: [], resolve: null };
      if (resolve) resolve(result);
    },
    getTableUrl(tableId) {
      return `/clinical/expense-tables/${tableId}/edit`;
    },
    blankForm() {
      return {
        id: null,
        name: '',
        type: '',
        record: '',
        active: true,
        returnDays: '',
        dailyLimit: '',
        notes: '',
        insurance: {
          id: '',
          name: '',
        },
        settings: {
          general: this.blankGeneralForm(),
          tiss: this.blankTissForm(),
        },
      };
    },
    blankGeneralForm() {
      return {
        aso: {
          requiredClient: false,
        },
        record: {
          required: false,
          requiredValidity: false,
          fillValidityWithServiceDate: false,
          digits: {
            enabled: false,
            min: '',
            max: '',
          },
        },
        billing: {
          blockValues: false,
          hideInsuranceValue: false,
          allowDifferentReferralTypes: false,
          billingForIndividualSessions: false,
          accounts: {
            enabled: false,
            quantity: '',
          },
          paymentDays: '',
          payment: this.blankPaymentForm(),
          submissionDays: DEFAULT_SUBMISSION_DAYS,
          appealDays: '',
          allowSecondAppeal: false,
          return: {
            enabled: false,
            days: '',
          },
          reducers: [],
        },
        financial: {
          methodId: '',
          categoryId: '',
          bankAccountId: '',
        },
        taxes: {
          iss: '',
          irpj: '',
          pis: '',
          csll: '',
          cofins: '',
          epp: '',
          admin: '',
          others: '',
        },
        providerReferral: {
          lastValue: '',
          mode: 'automatic',
        },
        table: {
          procedureId: null,
          examId: null,
          materialId: null,
          medicineId: null,
        },
      };
    },
    blankPaymentForm() {
      return {
        type: 'daysAfter',
        daysAfter: { days: '', from: 'sent' },
        fixedDays: { days: [], graceDays: '' },
        schedule: [],
        invoice: { required: false, deadlineDays: '' },
        nonBusinessDay: 'postpone',
      };
    },
    blankScheduleCycle() {
      return {
        cutoffDate: '',
        previewDate: '',
        invoiceDeadline: '',
        paymentDate: '',
        transferDate: '',
      };
    },
    toInputDate(value) {
      return value ? moment.utc(value).format('YYYY-MM-DD') : '';
    },
    paymentForm(payment, legacyDays) {
      const blank = this.blankPaymentForm();
      const numberOrBlank = value => (value === null || value === undefined ? '' : value);
      const positiveOrBlank = value => value || '';
      if (!payment) {
        blank.daysAfter.days = positiveOrBlank(legacyDays);
        return blank;
      }
      const daysAfter = payment.daysAfter || {};
      const fixedDays = payment.fixedDays || {};
      const invoice = payment.invoice || {};
      return {
        type: payment.type || blank.type,
        daysAfter: {
          days: positiveOrBlank(daysAfter.days || legacyDays),
          from: daysAfter.from || blank.daysAfter.from,
        },
        fixedDays: {
          days: [...(fixedDays.days || [])].map(Number),
          graceDays: numberOrBlank(fixedDays.graceDays),
        },
        schedule: (payment.schedule || []).map(cycle => ({
          cutoffDate: this.toInputDate(cycle.cutoffDate),
          previewDate: this.toInputDate(cycle.previewDate),
          invoiceDeadline: this.toInputDate(cycle.invoiceDeadline),
          paymentDate: this.toInputDate(cycle.paymentDate),
          transferDate: this.toInputDate(cycle.transferDate),
        })),
        invoice: {
          required: !!invoice.required,
          deadlineDays: positiveOrBlank(invoice.deadlineDays),
        },
        nonBusinessDay: payment.nonBusinessDay || blank.nonBusinessDay,
      };
    },
    paymentPayload() {
      const { payment } = this;
      const number = value => (value === '' || value === null || value === undefined
        ? null
        : Number(value));
      const dateOrNull = value => value || null;
      return {
        type: payment.type,
        daysAfter: {
          days: number(payment.daysAfter.days),
          from: payment.daysAfter.from,
        },
        fixedDays: {
          days: [...payment.fixedDays.days].map(Number).sort((a, b) => a - b),
          graceDays: number(payment.fixedDays.graceDays) || 0,
        },
        schedule: payment.schedule
          .map(cycle => ({
            cutoffDate: dateOrNull(cycle.cutoffDate),
            previewDate: dateOrNull(cycle.previewDate),
            invoiceDeadline: dateOrNull(cycle.invoiceDeadline),
            paymentDate: dateOrNull(cycle.paymentDate),
            transferDate: dateOrNull(cycle.transferDate),
          }))
          .sort((a, b) => String(a.cutoffDate).localeCompare(String(b.cutoffDate))),
        invoice: {
          required: !!payment.invoice.required,
          deadlineDays: payment.invoice.required ? number(payment.invoice.deadlineDays) : null,
        },
        nonBusinessDay: payment.nonBusinessDay,
      };
    },
    cycleValidator(index) {
      return this.$v.form.settings.general.billing.payment.schedule.$each[index];
    },
    toggleFixedDay(day) {
      const { days } = this.payment.fixedDays;
      const index = days.indexOf(day);
      if (index >= 0) days.splice(index, 1);
      else days.push(day);
      this.$v.form.settings.general.billing.payment.fixedDays.days.$touch();
    },
    addScheduleCycle() {
      this.payment.schedule.push(this.blankScheduleCycle());
    },
    removeScheduleCycle(index) {
      this.payment.schedule.splice(index, 1);
    },
    copyScheduleYear() {
      const year = this.scheduleLastYear;
      if (!year) return;
      const shift = (value) => {
        const parsed = moment(value, 'YYYY-MM-DD', true);
        return parsed.isValid() ? parsed.add(1, 'year').format('YYYY-MM-DD') : '';
      };
      const copies = this.payment.schedule
        .filter(cycle => moment(cycle.cutoffDate, 'YYYY-MM-DD', true).year() === year)
        .map(cycle => ({
          cutoffDate: shift(cycle.cutoffDate),
          previewDate: shift(cycle.previewDate),
          invoiceDeadline: shift(cycle.invoiceDeadline),
          paymentDate: shift(cycle.paymentDate),
          transferDate: shift(cycle.transferDate),
        }));
      this.payment.schedule.push(...copies);
      this.$toast.success(`${copies.length} ${copies.length === 1 ? 'ciclo copiado' : 'ciclos copiados'} para ${year + 1}. Revise as datas.`);
    },
    blankTissForm() {
      return {
        enabled: false,
        version: '',
        xml: {
          batch: '',
          fields: {
            insuranceReferral: false,
          },
        },
        contract: {
          type: 'company',
          code: '',
          identity: '',
          name: '',
          cnes: '',
        },
        referral: {
          origin: {
            type: 'company',
            value: 'identity',
          },
          requester: {
            type: 'company',
            value: 'identity',
          },
          executant: {
            type: 'company',
            value: 'identity',
          },
          professional: {
            type: 'company',
            value: 'identity',
          },
        },
        consultation: {
          sadt: false,
          requiredInsuranceReferral: false,
        },
        sadt: {
          fillRequestDataWithServiceDate: false,
          fillAuthorizationDateWithServiceDate: false,
          requiredRequestDate: false,
          authorization: {
            fillValidityDate: {
              enabled: false,
              days: '',
            },
            fillInsuranceReferralWithPassword: false,
            requiredMainReferral: false,
            requiredInsuranceReferral: false,
            requiredDate: false,
            requiredPassword: false,
            requiredValidity: false,
          },
        },
      };
    },
  },
};
</script>

<style lang="scss">
  @import '@/assets/scss/_variables.scss';
  .insurance-plan-page {
    .ip-head {
      .ip-head-eyebrow {
        color: $primary-color;
        font-size: $font-size-xs;
        font-weight: 600;
        letter-spacing: .04em;
        text-transform: uppercase;
      }
      .ip-head-hint {
        color: $gray-color-dark;
        font-size: $font-size-sm;
        margin-top: $layout-spacing-sm;
      }
      .ip-chips {
        display: flex;
        flex-wrap: wrap;
        gap: $layout-spacing-sm;
        margin-top: $layout-spacing;
      }
      .ip-chip {
        align-items: center;
        background-color: $gray-color-ultra-light;
        border: $border-width solid $border-color;
        border-radius: 1rem;
        color: $gray-color-dark;
        display: inline-flex;
        font-size: $font-size-xs;
        gap: .2rem;
        padding: .05rem .4rem;
        &.is-on {
          background-color: #F2FAF5;
          border-color: #BFE3CD;
          color: #1F7A4E;
        }
      }
    }
    .ip-errors {
      background-color: $red-color-100;
      border: $border-width solid $red-color-300;
      border-radius: $border-radius;
      margin-bottom: $layout-spacing-lg;
      padding: $layout-spacing $layout-spacing-lg;
      .ip-errors-head {
        align-items: center;
        color: $gray-color-900;
        display: flex;
        gap: $layout-spacing-sm;
        font-size: $font-size-sm;
        svg {
          color: $red-color-700;
        }
      }
      .ip-errors-list {
        display: flex;
        flex-wrap: wrap;
        gap: $layout-spacing-sm;
        margin-top: $layout-spacing;
      }
      .ip-error-field {
        background-color: $light-color;
        border: $border-width solid $border-color-dark;
        border-radius: $border-radius;
        color: $gray-color-900;
        cursor: pointer;
        font-size: $font-size-xs;
        font-weight: 600;
        line-height: 1.2;
        padding: .15rem .4rem;
        text-align: left;
        &:hover {
          border-color: $gray-color-dark;
        }
        small {
          color: $gray-color-dark;
          display: block;
          font-size: .5rem;
          font-weight: 400;
        }
      }
    }
    .tab .tab-badge {
      background-color: $error-color;
      border-radius: 50%;
      color: transparent;
      display: inline-block;
      font-size: 0;
      height: .3rem;
      line-height: 0;
      margin-left: .25rem;
      min-width: 0;
      overflow: hidden;
      padding: 0;
      vertical-align: middle;
      width: .3rem;
    }
    .ip-footer {
      align-items: center;
      background-color: $light-color;
      border-top: $border-width solid $border-color;
      bottom: 0;
      display: flex;
      gap: $layout-spacing;
      position: sticky;
      z-index: 2;
      .ip-footer-state {
        color: $red-color-700;
        font-size: $font-size-xs;
        margin-left: auto;
        svg {
          margin-right: .2rem;
        }
      }
      .ip-footer-schedule {
        margin-left: auto;
        + .ip-footer-state {
          margin-left: 0;
        }
      }
      .ip-footer-state + .ip-footer-schedule {
        margin-left: 0;
      }
    }
    .ip-section {
      background-color: $light-color;
      border: $border-width solid $border-color-dark;
      border-radius: $border-radius;
      margin-bottom: $layout-spacing-lg;
      &:last-child {
        margin-bottom: 0;
      }
      .ip-section-head {
        align-items: center;
        background-color: $gray-color-ultra-light;
        border-bottom: $border-width solid $border-color;
        border-radius: $border-radius $border-radius 0 0;
        display: flex;
        gap: $layout-spacing;
        padding: $layout-spacing $layout-spacing-lg;
      }
      .ip-section-icon {
        align-items: center;
        background-color: lighten($primary-color, 33%);
        border-radius: 50%;
        color: $primary-color;
        display: flex;
        flex-shrink: 0;
        height: 1.4rem;
        justify-content: center;
        width: 1.4rem;
      }
      .ip-section-text {
        display: flex;
        flex-direction: column;
        min-width: 0;
        strong {
          color: $gray-color-900;
          font-weight: 600;
        }
        small {
          color: $gray-color-dark;
          font-size: $font-size-xs;
          line-height: 1.2;
        }
      }
      .ip-section-aside {
        color: $gray-color-dark;
        font-size: $font-size-xs;
        margin-left: auto;
        text-align: right;
      }
      .ip-section-action {
        flex-shrink: 0;
        margin-left: auto;
      }
      .ip-section-body {
        padding: $layout-spacing-lg;
        > .columns:last-child .form-group:last-child {
          margin-bottom: 0;
        }
        .empty {
          padding: $layout-spacing-lg;
        }
      }
      .form-input-hint {
        color: $gray-color-dark;
      }
      .has-error .form-input-hint {
        color: $error-color;
      }
      .form-group > .form-input.text-center {
        font-variant-numeric: tabular-nums;
        max-width: 3.4rem;
      }
    }
    .ip-block {
      border-top: $border-width dashed $border-color;
      margin-top: $layout-spacing-lg;
      padding-top: $layout-spacing;
    }
    .ip-block-title {
      color: $gray-color-900;
      font-size: $font-size-sm;
      font-weight: 600;
      margin-bottom: $layout-spacing-sm;
    }
    .ip-notes {
      width: 100%;
    }
    .ip-field-action {
      align-self: flex-end;
    }
    .ip-inline-field {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: $layout-spacing;
      .form-label {
        flex-shrink: 0;
        padding: 0;
      }
      .input-group {
        flex: 0 0 auto;
        width: auto;
        .form-input {
          width: 2.8rem;
        }
      }
      .form-input,
      .form-select {
        flex-shrink: 0;
        width: auto;
      }
      .form-select {
        max-width: 12rem;
      }
      .form-input-hint {
        flex: 1 1 12rem;
        margin: 0;
      }
    }
    .input-group .input-group-addon {
      background-color: $gray-color-ultra-light;
      color: $gray-color-dark;
      font-size: $font-size-sm;
    }
    .ip-pct .form-input {
      font-variant-numeric: tabular-nums;
      text-align: right;
    }
    .ip-table-actions {
      border-top: $border-width dashed $border-color;
      margin-top: $layout-spacing;
      padding-top: $layout-spacing;
      text-align: right;
    }
    .ip-summary {
      align-items: flex-start;
      background-color: #FFF8E6;
      border: $border-width solid #F2C94C;
      border-radius: $border-radius;
      color: $gray-color-900;
      display: flex;
      font-size: $font-size-sm;
      gap: $layout-spacing-sm;
      margin-top: $layout-spacing-lg;
      padding: $layout-spacing $layout-spacing;
      svg {
        color: darken($warning-color, 22%);
        flex-shrink: 0;
        margin-top: .1rem;
      }
    }
    .ip-gate {
      align-items: center;
      background-color: $gray-color-ultra-light;
      border: $border-width dashed $border-color-dark;
      border-radius: $border-radius;
      display: flex;
      gap: $layout-spacing;
      padding: $layout-spacing-lg;
      .ip-gate-icon {
        align-items: center;
        background-color: $light-color;
        border-radius: 50%;
        color: $gray-color-dark;
        display: flex;
        flex-shrink: 0;
        height: 1.6rem;
        justify-content: center;
        width: 1.6rem;
      }
      .ip-gate-text {
        display: flex;
        flex-direction: column;
        min-width: 0;
        strong {
          color: $gray-color-900;
        }
        small {
          color: $gray-color-dark;
          font-size: $font-size-xs;
          line-height: 1.2;
        }
      }
      .btn {
        margin-left: auto;
      }
    }
    .columns + .ip-switches,
    .form-group + .ip-switches {
      margin-top: $layout-spacing-lg;
    }
    .ip-switches {
      border: $border-width solid $border-color;
      border-radius: $border-radius;
      overflow: hidden;
      .ip-switch-row {
        align-items: center;
        background-color: $light-color;
        border-bottom: $border-width solid $border-color;
        cursor: pointer;
        display: flex;
        gap: $layout-spacing-lg;
        margin: 0;
        padding: $layout-spacing $layout-spacing-lg;
        &:last-child {
          border-bottom: 0;
        }
        &:hover {
          background-color: $gray-color-ultra-light;
        }
      }
      .ip-switch-text {
        display: flex;
        flex: 1;
        flex-direction: column;
        min-width: 0;
        strong {
          color: $gray-color-900;
          font-weight: 500;
        }
        small {
          color: $gray-color-dark;
          font-size: $font-size-xs;
          line-height: 1.2;
        }
      }
      .ip-switch-field {
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        text-align: center;
        .input-group {
          flex-wrap: nowrap;
        }
        .form-input {
          font-variant-numeric: tabular-nums;
          width: 2.8rem;
        }
        .form-input-hint {
          color: $gray-color-dark;
          font-size: $font-size-xs;
          margin: 0;
          white-space: nowrap;
        }
        &.has-error .form-input-hint {
          color: $error-color;
        }
      }
      .ip-switch-inline {
        display: flex;
        gap: $layout-spacing-sm;
      }
      .form-switch {
        flex-shrink: 0;
        margin: 0;
        padding: 0 0 0 1.2rem;
      }
    }
    .ip-choice-rows {
      border: $border-width solid $border-color;
      border-radius: $border-radius;
      overflow: hidden;
      .ip-choice-row {
        align-items: center;
        background-color: $light-color;
        border-bottom: $border-width solid $border-color;
        cursor: pointer;
        display: flex;
        gap: $layout-spacing;
        margin: 0;
        padding: $layout-spacing $layout-spacing-lg;
        &:last-child {
          border-bottom: 0;
        }
        &:hover {
          background-color: $gray-color-ultra-light;
        }
        &.is-active {
          background-color: lighten($primary-color, 36%);
        }
      }
      .ip-choice-text {
        display: flex;
        flex: 1;
        flex-direction: column;
        min-width: 0;
        strong {
          color: $gray-color-900;
          font-weight: 500;
        }
        small {
          color: $gray-color-dark;
          font-size: $font-size-xs;
          line-height: 1.2;
        }
      }
      .ip-choice-field {
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        text-align: center;
        .form-input {
          font-variant-numeric: tabular-nums;
          width: 6rem;
        }
        .form-input-hint {
          color: $gray-color-dark;
          font-size: $font-size-xs;
          margin: 0;
        }
        &.has-error .form-input-hint {
          color: $error-color;
        }
      }
      .form-radio {
        flex-shrink: 0;
        margin: 0;
        padding: 0 0 0 .8rem;
      }
    }
    .ip-payment {
      .rule-cards {
        display: flex;
        flex-wrap: wrap;
        gap: $layout-spacing;
        margin-bottom: $layout-spacing-lg;
      }
      .rule-card {
        align-items: center;
        background-color: $gray-color-ultra-light;
        border: $border-width solid $border-color-dark;
        border-radius: $border-radius;
        cursor: pointer;
        display: flex;
        flex: 1 1 13rem;
        gap: $layout-spacing;
        max-width: 16rem;
        padding: $layout-spacing;
        transition: border-color .15s ease, background-color .15s ease;
        &:hover {
          border-color: $primary-color;
        }
        .rule-icon {
          align-items: center;
          background-color: $light-color;
          border-radius: 50%;
          color: $primary-color;
          display: flex;
          flex-shrink: 0;
          height: 1.4rem;
          justify-content: center;
          width: 1.4rem;
        }
        .rule-text {
          display: flex;
          flex-direction: column;
          min-width: 0;
          strong {
            color: $gray-color-900;
          }
          small {
            color: $gray-color-dark;
            font-size: $font-size-xs;
            line-height: 1.2;
          }
        }
        .rule-check {
          color: $primary-color;
          margin-left: auto;
        }
        &.is-active {
          background-color: lighten($primary-color, 36%);
          border-color: $primary-color;
          .rule-icon {
            background-color: $primary-color;
            color: $light-color;
          }
        }
      }
      .rule-extra {
        border-top: $border-width dashed $border-color;
        margin-top: $layout-spacing;
        padding-top: $layout-spacing;
      }
      .payment-days-grid {
        display: grid;
        gap: $layout-spacing-sm;
        grid-template-columns: repeat(16, minmax(0, 1fr));
        max-width: 38rem;
        .payment-day {
          background-color: $light-color;
          border: $border-color-dark solid $border-width;
          border-radius: $border-radius;
          color: $gray-color-dark;
          cursor: pointer;
          font-size: $font-size-xs;
          font-variant-numeric: tabular-nums;
          height: 1.5rem;
          padding: 0;
          &:hover {
            border-color: $primary-color;
            color: $primary-color;
          }
          &.active {
            background-color: $primary-color;
            border-color: $primary-color;
            color: $light-color;
            font-weight: 600;
          }
        }
      }
      .payment-schedule {
        .payment-schedule-head {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: $layout-spacing-sm;
          .form-label {
            align-items: center;
            display: flex;
            flex: 1;
            gap: $layout-spacing-sm;
            padding: 0;
          }
          .schedule-count {
            background-color: $gray-color-ultra-light;
            border-radius: 1rem;
            color: $gray-color-dark;
            font-size: $font-size-xs;
            font-weight: 400;
            padding: 0 .3rem;
          }
        }
        .payment-schedule-table {
          margin-top: $layout-spacing;
          td {
            border-bottom: $border-width solid $border-color;
            font-size: $font-size-sm;
            padding: $layout-spacing $layout-spacing-sm;
            vertical-align: top;
          }
          th {
            font-size: $font-size-xs;
            line-height: 1.2;
            padding: $layout-spacing-sm $layout-spacing-sm;
            small {
              color: $gray-color-dark;
              display: block;
              font-size: .5rem;
              font-weight: 400;
            }
            &:last-child {
              width: 3rem;
            }
          }
          tbody tr:hover {
            background-color: $gray-color-ultra-light;
          }
          .form-input {
            width: 6.4rem;
          }
        }
        .payment-schedule-empty {
          align-items: center;
          background-color: $gray-color-ultra-light;
          border: $border-width dashed $border-color-dark;
          border-radius: $border-radius;
          display: flex;
          gap: $layout-spacing;
          margin-top: $layout-spacing-sm;
          padding: $layout-spacing;
          .empty-icon {
            align-items: center;
            background-color: $light-color;
            border-radius: 50%;
            color: $primary-color;
            display: flex;
            flex-shrink: 0;
            height: 1.6rem;
            justify-content: center;
            width: 1.6rem;
          }
          .empty-text {
            display: flex;
            flex-direction: column;
            min-width: 0;
            strong {
              color: $gray-color-900;
            }
            small {
              color: $gray-color-dark;
              font-size: $font-size-xs;
              line-height: 1.2;
            }
          }
          .btn {
            margin-left: auto;
          }
          &.is-error {
            background-color: #FFF5F5;
            border-color: $error-color;
            .empty-icon {
              color: $error-color;
            }
            .empty-text strong {
              color: $error-color;
            }
          }
        }
      }
    }
    .tab {
      margin-bottom: $layout-spacing-lg;
      .tab-item a {
        white-space: nowrap;
      }
    }
    .tab-panel {
      padding-bottom: $layout-spacing-lg;
    }
    .table {
      margin-top: $layout-spacing;
    }
    .table td {
      font-size: .55rem;
    }
    .table th {
      background-color: $gray-color-ultra-light;
    }
    .input-group .form-select {
      width: 100%;
    }
  }
</style>
